import * as THREE from "three";
import * as TWEEN from "@tweenjs/tween.js";
import System, {SpriteRenderer} from "three-nebula";
import {computeBoundsTree, disposeBoundsTree} from "three-mesh-bvh";

import {DragControls} from "three/examples/jsm/controls/DragControls";
import Renderer from "./Assets/Renderer.js";
import {CLIENT_MESSAGE} from "@redbrick-sdk/multiplay-common";
import {URL, CHAT_ONE_LINE_CHAR_LIMIT, IPX_PID, PID, REDBRICK_EVENT_PID, PROPERTY_TAB} from "utils/constants";
import {donutNpcSettings, npcSettings} from "utils/constants";
// @ts-ignore
import {JoyStick} from "./Libs/JoyStick";
import {MeshoptDecoder} from "meshoptimizer";

// import { CSG } from '@enable3d/three-graphics/jsm/csg'
import {GLTFLoader} from "three/examples/jsm/loaders/GLTFLoader";
import {FontLoader} from "three/examples/jsm/loaders/FontLoader.js";
import {TextGeometry} from "three/examples/jsm/geometries/TextGeometry";
import Stats from "three/examples/jsm/libs/stats.module"; // TMP remove later

import {GLTFExporter} from "three/examples/jsm/exporters/GLTFExporter.js";

import {_updateProperty} from "./index_updateProperty.js";
import {_undoRedo, createActionStack} from "./index_undoRedo.js";
import FirstPersonControls from "./Libs/FirstPersonControls.js";
import {_playModeFetchFromS3, _playModeFetchFromLocal, _editModeFetchFromS3, _editModeFetchFromS3CopyPID} from "./index_s3Loader.js";
import {_autoSaveProject, _saveProject, _publishWorld} from "./index_s3Uploader.js";
import {_loadFile, _saveFile} from "./index_saveLoadFile.js";
import {_setupPhysics} from "./index_setupPhysics.js";
import {_setupPhysicsRevise} from "./index_setupPhysics_revise.js";
import GameServer from "./GameServer.js";
import GameServerV1 from "./GameServerV1.js";
import {deviceCheck} from "utils/deviceChecker";
import VersionManager from "utils/VersionManager.js";
import ScriptManager from "utils/UserScript/ScriptManager.js";
import ScriptLauncher from "utils/UserScript/ScriptLauncher.js";
import VRControl from "./Libs/VRControl.js"; //VR Controller
import {VRButton} from "three/examples/jsm/webxr/VRButton.js"; //VR
import loadingAnimation from "../../../images/common/loading_animation.gif";

import {
  TRANSFORM_TYPE,
  ACTION_TYPE,
  AVATAR_ANIMATION,
  EDITORMODE,
  SELFIE,
  CHAT_TYPE,
  SELFIE_CAM_WIDTH,
  SELFIE_CAM_HEIGHT,
} from "utils/constants";
import {PlayControls} from "./Libs/PlayControls.js";
import {PlayControls_MouseLock} from "./Libs/PlayControls_MouseLock.js";
import {SelfieControls} from "./Libs/SelfieControls";

import {MODE_TYPE} from "utils/constants";
import Light from "./Assets/Light";
import Mesh from "./Assets/Mesh";
import World from "./Assets/World";
import Camera from "./Assets/Camera";
import StartLocation from "./Assets/StartPoint.js";
import Video from "./Assets/Video.js";
import Image from "./Assets/Image.js";
import Audio from "./Assets/Audio.js";
import Demo from "./Assets/Demo";
import Glb from "./Assets/Glb";
import Portal from "./Assets/Portal";
import CustomParticleSystem from "./Assets/ParticleSystem.js";
import OOBCApi from "utils/oobcApi.js";
import {ImageCompressor} from "utils/FileCompressor";
import {isMacOs, isMobile} from "react-device-detect";
import Command from "./command";
import {CSS2DRenderer, CSS2DObject} from "three/examples/jsm/renderers/CSS2DRenderer";
import {PositionalAudioHelper} from "three/examples/jsm/helpers/PositionalAudioHelper.js";

// Tutorial
import OnlineCodingParty from "./Tutorials/OnlineCodingParty.js";
import _ from "lodash";
import {ASSET_TYPE} from "utils/constants/assets.js";
import {decompress} from "compress-json";
import PathTracer from "./Assets/PathTracer.js";
import NPC from "./Assets/NPC.js";
import debuggerUI from "./Libs/debuggerUI.js";
import {updateWorldMonitoring} from "api-v3/worldMonitoring.js";
import {getFullAssetUrl} from "utils/Validate.js";

// Images
import saveSelfieImage from "images/common/selfie-save.png";
// sample games
// import deliveryGo from "assets/sampleGames/deliveryGo.json";
// import jump from "assets/sampleGames/jump.json";

class Graphics extends EventTarget {
  constructor(canvas) {
    super();

    window.turnBackgroundMode = this.turnBackgroundMode.bind(this);
    window.isBackgroundMode = false;
    THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
    THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;

    const queryParams = new URLSearchParams(window.location.search);
    this.pathTracers = [];
    this.NPCs = [];

    this.template = queryParams.get("template");

    this.gender = queryParams.get("gender");
    this.demo = queryParams.get("demo");
    this.model = queryParams.get("model");
    this.ballstopcle = queryParams.get("ballstopcle");
    this.runMode = queryParams.get("mode");
    this.pId = queryParams.get("pid");
    this.devLang = queryParams.get("devlang");
    this.isGuestMode = queryParams.get("isGuest");
    this.renderingMode = queryParams.get("renderingMode");
    this.isTouchDevice = "ontouchstart" in window;
    this.contentsType = queryParams.get("contentsType");

    this.fromIpx = _.includes(IPX_PID, this.pId);
    this.isDevEnv = window.location.hostname === "dev.redbrick.land" || window.location.hostname === "localhost";

    this.iconUrl = undefined;
    this.gameUrl = undefined;

    this.userInfo = undefined;
    this.chatMode = false;
    this.cameraMode = "NORMAL";
    this.landMode = false;

    this.canvas = canvas;
    this.renderer = new Renderer(canvas);
    this.animationFrameCallbacks = {};
    this.renderer.glRenderer.setAnimationLoop(time => Object.values(this.animationFrameCallbacks).forEach(callback => callback(time)));

    // for camera preview
    this.previewRenderTarget = new THREE.WebGLRenderTarget(512, 512);
    const previewCanvas = new THREE.Sprite(new THREE.SpriteMaterial({map: this.previewRenderTarget.texture}));
    previewCanvas.position.set(0, 0, 1);
    previewCanvas.scale.set(0.96, 0.96, 1);
    this.previewSprite = new THREE.Sprite(new THREE.SpriteMaterial({color: "black"}));
    this.previewSprite.add(previewCanvas);
    this.previewScene = new THREE.Scene();
    this.previewScene.add(this.previewSprite);

    this.currentDevice = deviceCheck();
    this.selfieCamWidth = SELFIE_CAM_WIDTH[this.currentDevice];
    this.selfieCamHeight = SELFIE_CAM_HEIGHT[this.currentDevice];

    this.labelRenderer = new CSS2DRenderer();
    this.labelRenderer.setSize(window.innerWidth, window.innerHeight);
    this.labelRenderer.domElement.style.position = "absolute";
    this.labelRenderer.domElement.style.top = "0px";

    const canvasParent = document.querySelector("#canvas_container");
    canvasParent.appendChild(this.labelRenderer.domElement);

    this.canvasObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        this.resizeHandler_(entry.contentRect.width, entry.contentRect.height);
      });
    });

    this.clock = new THREE.Clock();

    this.rayCaster = new THREE.Raycaster();
    this.rayCaster.layers.enableAll();
    this.rayCaster.layers.disable(4);
    this.GLTFLoader = new GLTFLoader();
    this.TextureLoader = new THREE.TextureLoader();
    this.cubeTextureLoader = new THREE.CubeTextureLoader();

    this.previousCameraOffset = new THREE.Vector3();
    this.rayOriginV3 = new THREE.Vector3();
    this.rayDirV3 = new THREE.Vector3();
    this.camDistance = 0;
    this.playCameraRaycaster = new THREE.Raycaster(this.rayOriginV3, this.rayDirV3);
    this.playCameraRaycaster.layers.enableAll();
    // Array of meshes to be used for camera colliding
    this.playCamIntersects = [];
    this.objectObstructionOptions = [false, false, false, false];
    this.objects = [];
    this.objects2D = [];
    this.helpers = [];

    this.assetObjects = {};
    this.videoElements = {};

    this.sceneEdit = undefined;
    this.sceneHelpers = undefined;
    this.scenePlay = undefined;
    this.scenePlayObjects = undefined;
    this.physics = undefined;
    this.showSceneHelpers = true;
    this.showGrid = false;
    this.mode = MODE_TYPE.EDIT_MODE;
    this.renderer.glRenderer.info.autoReset = false;
    this.avatar = undefined;
    this.isMoving = false;
    this.isAvatarReady = false;

    this.audioListener = new THREE.AudioListener();
    this.audioHelper = new THREE.PositionalAudio(this.audioListener);
    this.audioHelper.setDirectionalCone(360, 0, 0);
    this.audioRefDistanceRange = new PositionalAudioHelper(this.audioHelper, 1, 16, 4);
    this.audioMaxDistanceRange = new PositionalAudioHelper(this.audioHelper, 1, 16, 4);
    this.audioHelper.add(this.audioRefDistanceRange, this.audioMaxDistanceRange);
    this.audioHelper.traverse(child => (child.visible = false));

    this.transformDraggingStart = true;
    this.transformStartValue = {};
    this.undo = createActionStack({
      onChangeLength: (prevLen, len) => {
        if (prevLen === 0 && len !== 0) {
          this.dispatchEvent(new CustomEvent(ACTION_TYPE.TOGGLE_UNDO_ABLE, {detail: true}));
        } else if (prevLen !== 0 && len === 0) {
          this.dispatchEvent(new CustomEvent(ACTION_TYPE.TOGGLE_UNDO_ABLE, {detail: false}));
        }
      },
    });
    this.redo = createActionStack({
      onChangeLength: (prevLen, len) => {
        if (prevLen === 0 && len !== 0) {
          this.dispatchEvent(new CustomEvent(ACTION_TYPE.TOGGLE_REDO_ABLE, {detail: true}));
        } else if (prevLen !== 0 && len === 0) {
          this.dispatchEvent(new CustomEvent(ACTION_TYPE.TOGGLE_REDO_ABLE, {detail: false}));
        }
      },
    });
    this.lastCmdTime = new Date();

    // for setting object spawn point in 'postAddObject'
    this._cameraDirection = new THREE.Vector3();
    this._cameraRaycaster = new THREE.Raycaster();
    this._cameraRaycaster.far = 200;
    this._cameraRaycaster.layers.enableAll();
    this._cameraRaycaster.layers.disable(4);
    this._spawnPoint = new THREE.Vector3();
    this._boundingBox = new THREE.Box3();
    this.yAxis = new THREE.Vector3(0, 1, 0);

    this.stats = Stats();
    this.debuggerUI = new debuggerUI(this);
    this.debuggerUI.init();

    this.showStats = false;
    this.statsDom = document.body.appendChild(this.stats.dom);
    this.statsDom.style.display = "none";

    this.moveDirection = {left: 0, right: 0, forward: 0, back: 0, jump: 0};
    this.isAvatarControllerActive = true;

    this.transformType = TRANSFORM_TYPE.TRANSLATE;
    this.enableMultipleSelect = false;

    // for multipleselect
    this.boxCenter = new THREE.Vector3();
    this.lastBoxCenter = new THREE.Vector3();

    this.resizeHandler = this.resizeHandler_.bind(this);
    this.visibilityHandler = this.visibilityHandler_.bind(this);
    this.copyObject.bind(this);
    this.removeObject.bind(this);
    this.changeMode.bind(this);
    this.saveSelfie = this.saveSelfie_.bind(this);
    this.onMouseDown = this.onMouseDown_.bind(this);
    // TEMP : audio play
    this.mousedownFlag = false;
    this.onMouseUp = this.onMouseUp_.bind(this);
    this.onMouseMove = this.onMouseMove_.bind(this);
    this.onDrop = this.onDrop_.bind(this);
    // this.onDoubleClick = this.onDoubleClick_.bind(this);
    this.editorKeyDownHandler = this.editorKeyDownHandler_.bind(this);
    this.editorKeyUpHandler = this.editorKeyUpHandler_.bind(this);
    this.playKeyUpHandler = this.playKeyUpHandler_.bind(this);
    this.playKeyDownHandler = this.playKeyDownHandler_.bind(this);
    //VR handlers
    this.onVRControllerLeftSqueezeStart = this.onVRControllerRightSqueezeStart_.bind(this);
    this.onVRControllerLeftSqueezeEnd = this.onVRControllerRightSqueezeEnd_.bind(this);
    this.onVRControllerRightSqueezeStart = this.onVRControllerRightSqueezeStart_.bind(this);
    this.onVRControllerRightSqueezeEnd = this.onVRControllerRightSqueezeEnd_.bind(this);
    this.renderer.glRenderer.xr.addEventListener("sessionstart", () => {
      this.isTouchDevice = false;
      this.onSwitchVRMode(true);
    });
    this.renderer.glRenderer.xr.addEventListener("sessionend", () => {
      this.onSwitchVRMode(false);
      this.isTouchDevice = "ontouchstart" in window;
    });

    this.dragstartSprite = this.dragstartSprite_.bind(this);
    this.dragendSprite = this.dragendSprite_.bind(this);

    this.onDownPosition = new THREE.Vector2();
    this.onUpPosition = new THREE.Vector2();
    this.cursorPosition = new THREE.Vector2();
    this.pressedKeys = {};
    this.isClicked = false;
    this.keyDownCallbacks = {};
    this.keyUpCallbacks = {};

    this.avatarPositionQueue = [];

    this.particleSystemArray = [];
    this.editParticleSystemArray = [];
    this.joystickRadian = 0;
    this.joystickEnabled = true;
    const isDynamicJoystick = queryParams.get("djs"); // [TMP]
    this.joystick = new JoyStick(isDynamicJoystick);
    this.buttonAttack = this.joystick.add.button({
      letter: "A",
      styles: {
        right: window.innerWidth < 768 ? 10 : 35,
        bottom: window.innerWidth < 768 ? 57 : 110,
        size: window.innerWidth < 768 ? 75 : 80,
      },
    });
    this.buttonAttack.onClick(() => {});
    this.buttonJump = this.joystick.add.button({
      letter: "J",
      styles: {
        right: window.innerWidth < 768 ? 87 : 110,
        bottom: window.innerWidth < 768 ? 10 : 35,
        size: window.innerWidth < 768 ? 75 : 80,
      },
    });
    this.buttonJump.onClick(() => {
      if (this.joystickEnabled) {
        this.moveDirection.jump = 1;
      }
    });
    this.buttonJump.onRelease(() => (this.moveDirection.jump = 0));
    this.joystick.hide();

    window.addEventListener("resize", this.resizeHandler);
    window.addEventListener("visibilitychange", this.visibilityHandler);
    window.addEventListener("message", event => {
      switch (event.data.type) {
        case "resize":
          this.resizeHandler_();
          break;
        default:
          break;
      }
    });
    this.labelRenderer.domElement.addEventListener("mousedown", this.onMouseDown, false);
    this.labelRenderer.domElement.addEventListener("mousemove", this.onMouseMove);
    this.labelRenderer.domElement.addEventListener("drop", this.onDrop);
    this.labelRenderer.domElement.addEventListener("dragover", function (event) {
      event.preventDefault();
    });

    // this.canvas.addEventListener('dblclick', this.onDoubleClick, false);
    document.addEventListener("keydown", this.editorKeyDownHandler);
    document.addEventListener("keyup", this.editorKeyUpHandler);
    this.observeResizingCanvas();

    this.box = new THREE.Box3();
    this.selectionBox = new THREE.BoxHelper();
    this.selectionBox.material.depthTest = false;
    this.selectionBox.material.transparent = true;
    this.selectionBox.visible = false;

    this.world = new World(this, "default");

    this.currentProperyTab = PROPERTY_TAB.OBJECT;

    this.transform.addEventListener("dragging-changed", event => {
      let target;

      this.orbit.keyActive = {LEFT: false, UP: false, RIGHT: false, BOTTOM: false, FORWARD: false, SHIFT: false, BACKWARD: false};
      this.orbit.enabled = !event.value;
      if (this.transformDraggingStart) {
        this.transformDraggingStart = false;
        this.transformStartValue = {
          position: this.selected.position.clone(),
          rotation: this.selected.rotation.clone(),
          scale: this.selected.scale.clone(),
        };
      } else {
        this.selected.userData.positionX = this.selected.position.x;
        this.selected.userData.positionY = this.selected.position.y;
        this.selected.userData.positionZ = this.selected.position.z;
        this.selected.userData.rotationX = this.selected.rotation.x;
        this.selected.userData.rotationY = this.selected.rotation.y;
        this.selected.userData.rotationZ = this.selected.rotation.z;
        this.selected.userData.scaleX = this.selected.scale.x;
        this.selected.userData.scaleY = this.selected.scale.y;
        this.selected.userData.scaleZ = this.selected.scale.z;

        if (this.selected === this.tempGroup) {
          this.box.setFromObject(this.tempGroup);
          this.box.getCenter(this.lastBoxCenter);
          this.box.getCenter(this.boxCenter);
          target = "tempGroup";
        } else {
          target = this.selected;
        }

        this.dispatchEvent(
          new CustomEvent(ACTION_TYPE.CHANGE_PROPERTY_FROM_SCENE, {
            detail: {object: this.selected},
          }),
        );
        this.transformDraggingStart = true;

        let transformEndValue = {
          position: this.selected.position.clone(),
          rotation: this.selected.rotation.clone(),
          scale: this.selected.scale.clone(),
        };

        let obj = {
          type: Command.typeTransform,
          target: target,
          preValue: this.transformStartValue,
          postValue: transformEndValue,
        };

        let command = new Command(obj);
        this.undo.push(command);
        this.redo.length = 0;
      }
    });

    this.sceneHelpers.add(this.selectionBox);
    this.sceneHelpers.add(this.audioHelper);
    this.selected = undefined;

    this.versionManager = new VersionManager(this);
    this.scriptManager = new ScriptManager(this);

    this.updateCodeEditorMode();
    this.setupLatestVersion(); // for local test (without fetching scene)

    if (this.runMode === "play") {
      let url;
      if (!this.pId) this.pId = "a1";
      url = URL.S3_WORLD_STORAGE + "/" + this.pId + ".json";
      _playModeFetchFromS3(this, url);
    } else if (this.template) {
      if (this.runMode === "learn") this.tutorial = new OnlineCodingParty(this);
      let url = URL.S3_PROJECT_TEMPLATE_STORAGE + "/" + this.template + ".json";
      _editModeFetchFromS3(this, url);
    } else if (this.runMode === "edit") {
      let url = URL.S3_PROJECT_STORAGE + "/" + this.pId + ".json";
      _editModeFetchFromS3(this, url);
    } else if (this.runMode === "learn") {
      this.tutorial = new OnlineCodingParty(this);
      let url = URL.S3_PROJECT_STORAGE + "/" + this.pId + ".json";
      _editModeFetchFromS3(this, url);
    }

    this.fogExponential = new THREE.FogExp2("#ffffff", 0.025);
    this.fogLinear = new THREE.Fog("#ffffff", 1, 100);
    this.render();
  }

  addJoystick(isDynamic) {
    if (isDynamic) {
      this.joystick.createJoystickGuide();
    } else {
      this.joystickAxis =
        this.joystickAxis ||
        this.joystick.add.axis({
          styles: {
            left: window.innerWidth < 768 ? 40 : 35,
            bottom: window.innerWidth < 768 ? 15 : 35,
            size: window.innerWidth < 768 ? 75 : 100,
          },
        });

      const circle = document.getElementById("joystick");
      circle.style.display = "";
      circle.style.cssText =
        "background: rgba(255, 255, 255, 0.3);position: fixed;width: 96px;height: 96px;cursor: pointer;bottom: 48px;left: 72px;border-radius: 100%;";

      this.joystickAxis.onMove(event => {
        if (!this.joystickEnabled) {
          this.joystickPosition = {top: 0, right: 0};
          this.joystickRadian = 0;
          return;
        }
        this.joystickPosition = event;
        this.joystickRadian = -Math.atan2(event.right, event.top);
      });
    }
  }
  handleJoystickDirectionChange(x, y) {
    if (x === 0 && y === 0) {
      this.moveDirection.forward = 0;
      this.moveDirection.back = 0;
      this.moveDirection.left = 0;
      this.moveDirection.right = 0;
    } else if (this.avatar.model !== undefined && this.isAvatarControllerActive) {
      if (x > 0.3) {
        this.moveDirection.right = 1;
        this.moveDirection.left = 0;
      } else if (x < -0.3) {
        this.moveDirection.right = 0;
        this.moveDirection.left = 1;
      } else {
        this.moveDirection.right = 0;
        this.moveDirection.left = 0;
      }
      if (y > 0.3) {
        this.moveDirection.forward = 0;
        this.moveDirection.back = 1;
      } else if (y < -0.3) {
        this.moveDirection.back = 0;
        this.moveDirection.forward = 1;
      } else {
        this.moveDirection.back = 0;
        this.moveDirection.forward = 0;
      }
      this.setEmoteAnimation(undefined);
    }
  }
  removeJoystick(isDynamic) {
    this.joystick.removeAxis();
    delete this.joystickAxis;
    if (isDynamic) this.joystick.removeJoystickGuide();
  }

  setVRSession({VRCamera, VRObject}) {
    this.renderer.glRenderer.xr.enabled = true;
    this.VRCamera = VRCamera;
    this.VRObject = VRObject;

    document.body.appendChild(VRButton.createButton(this.renderer.glRenderer));
  }

  observeResizingCanvas() {
    this.canvasObserver.observe(this.canvas);
  }

  updateUserInfo(userInfo) {
    this.userInfo = userInfo;
    this.updateCodeEditorMode();
  }

  updateCodeEditorMode() {
    let codeEditorMode = EDITORMODE.BLOCK;
    if (this.runMode === "play") {
      if (this.scenePlay && this.scenePlay.userData.codeEditorMode === "text") {
        codeEditorMode = EDITORMODE.JAVASCRIPT_3D;
      }
    } else {
      if (this.template) {
        this.sceneEdit.userData.codeEditorMode = undefined;
      }
      if (this.sceneEdit.userData.codeEditorMode === "text") {
        codeEditorMode = EDITORMODE.JAVASCRIPT_3D;
      } else if (this.sceneEdit.userData.codeEditorMode === undefined) {
        if (this.devLang === "text") {
          codeEditorMode = EDITORMODE.JAVASCRIPT_3D;
          this.sceneEdit.userData.codeEditorMode = "text";
        } else {
          this.sceneEdit.userData.codeEditorMode = "oobc";
        }
      }
    }
    this.codeEditorMode = codeEditorMode;
    this.dispatchEvent(
      new CustomEvent(ACTION_TYPE.SET_CODE_EDITOR_MODE, {
        detail: codeEditorMode,
      }),
    );
    this.versionManager.parseVersion();
  }

  adBannerEventDispatch(event) {
    const {type, value} = event;
    if (type === "show") {
      this.dispatchEvent(new CustomEvent(ACTION_TYPE.CHANGE_AD_SHOW_STATE, {detail: value}));
    }
  }

  // Dispatch action when game calls event
  dispatchFromGameEvent(type, value) {
    switch (type) {
      case "state":
        this.dispatchEvent(
          new CustomEvent(ACTION_TYPE.SET_GAME_STATE, {
            detail: value,
          }),
        );
        break;
      case "message":
        this.dispatchEvent(
          new CustomEvent(ACTION_TYPE.RECEIVE_GAME_MESSAGE, {
            detail: value,
          }),
        );
        break;
      default:
        break;
    }
  }

  // Send redux state change to game
  sendMessageToGame({message}) {
    // IPX SPECIFIC - NPC CHAT
    if (this.pId === IPX_PID.NEWYORK && message.type === "CHAT_END") {
      let target = null;

      this.NPCs.forEach(NPC => {
        if (NPC.model.name === message.name) {
          target = NPC;
          NPC.showPanel();
          NPC.chatMode();
        }
      });
      this.pathTracers.forEach(tracer => {
        if (tracer.object.name === message.name) {
          tracer.pause();
          this.playControls.playFocus(target.model);
          this.scenePlay.enableKeyControl(true);
          tracer.actionType = message.previousAction;
        }
      });
      return;
    }
    if (this.gameServer.room) {
      this.gameServer.room.send("REDUX_CHANGED", message);
    } else {
      this.scriptLauncher.receiveReduxMessage(message);
    }
  }

  // TEMP : NEWYORK AVATAR SETTING FUNCTION
  setNpcs(userData) {
    for (let i = 0; i < userData.length; i++) {
      let setting = userData[i];

      this.GLTFLoader.load(setting.model, obj => {
        let model;
        model = obj.scene;
        model.name = setting.name;
        model.animations = obj.animations;
        model.scale.set(setting.scale.x, setting.scale.y, setting.scale.z);
        model.children.forEach(element => {
          element.rotateOnAxis(model.up, setting.rotationCalibration);
        });

        const npc = new NPC(model, this.avatar.model);
        npc.fixed = setting.fixed;
        npc.textSet = setting.textSet;
        npc.defaultAnimationIndex = setting.defaultAnimation;
        npc.model.position.set(setting.position.x, setting.position.y, setting.position.z);
        npc.model.rotation.set(setting.rotation.x, setting.rotation.y, setting.rotation.z);
        npc.setAnimation();
        if (setting.status !== undefined) {
          npc.changeStatus(setting.status);
        }
        if (setting.detectDist !== undefined) {
          npc.detectDist = setting.detectDist;
        }
        this.NPCs.push(npc);

        let pointsPath = new THREE.CurvePath();
        const points = [];
        if (setting.path) {
          setting.path.forEach(point => {
            points.push(new THREE.Vector3(point.x, point.y, point.z));
          });
          const curve = new THREE.CatmullRomCurve3(points);
          if (setting.tension !== undefined) {
            curve.tension = setting.tension;
          }
          if (setting.curveType !== undefined) {
            curve.curveType = setting.curveType;
          }
          pointsPath.add(curve);
        } else {
          pointsPath = null;
        }
        const pathTracer = new PathTracer(npc.model, pointsPath, this.avatar.model);
        pathTracer.speed = setting.pathOptions.speed;
        pathTracer.actionType = setting.pathOptions.actionType;
        pathTracer.moveDirection = setting.pathOptions.moveDirection;
        pathTracer.rotationOptions = setting.pathOptions.rotationOptions;
        pathTracer.lookOptions = setting.pathOptions.lookOptions;

        // pathTracer.setDefaultPath();
        this.pathTracers.push(pathTracer);
        this.scenePlay.add(npc.model);
      });
    }
  }

  editorKeyDownHandler_(event) {
    if (document.activeElement.tagName !== "BODY") return;
    switch (event.code) {
      case "KeyG":
        event.preventDefault();
        if (isMacOs ? event.metaKey : event.ctrlKey) {
          this.groupObject();
        }
        break;
      case "KeyS":
        if (isMacOs ? event.metaKey : event.ctrlKey) {
          event.preventDefault();
          this.saveProject();
        }
        break;
      case "Digit2":
        this.dispatchEvent(
          new CustomEvent(TRANSFORM_TYPE.CHANGE_TRANSFORM, {
            detail: TRANSFORM_TYPE.TRANSLATE,
          }),
        );
        break;
      case "Digit3":
        this.dispatchEvent(
          new CustomEvent(TRANSFORM_TYPE.CHANGE_TRANSFORM, {
            detail: TRANSFORM_TYPE.ROTATE,
          }),
        );
        break;
      case "Digit4":
        this.dispatchEvent(
          new CustomEvent(TRANSFORM_TYPE.CHANGE_TRANSFORM, {
            detail: TRANSFORM_TYPE.SCALE,
          }),
        );
        break;
      case "Digit9":
        if (this.orbit.keyActive.SHIFT) return;

        if (this.orbit.rotationMode === "ORBIT") {
          this.orbit.rotationMode = "CENTER";
          this.orbit.panSpeed = 80;
          this.orbit.keyPanSpeed = 200;
        } else {
          this.orbit.rotationMode = "ORBIT";
          this.orbit.panSpeed = 1;
          this.orbit.keyPanSpeed = 7;
        }
        break;
      case "ShiftLeft":
        if (this.selected !== undefined) {
          this.dispatchEvent(new CustomEvent(TRANSFORM_TYPE.SNAP_ON, {}));
        }
        break;
      case isMacOs ? "MetaLeft" : "ControlLeft":
        this.enableMultipleSelect = true;
        break;
      case "Delete":
        if (this.selected !== undefined) {
          this.removeObject(this.selected);
        }
        break;
      case "KeyI":
        if (event.ctrlKey) {
          if (this.showStats === false) {
            this.renderer.glRenderer.info.autoReset = false;
            this.debuggerUI.show();
            this.statsDom.style.display = "";
            this.showStats = true;
          } else {
            this.renderer.glRenderer.info.autoReset = true;
            this.debuggerUI.hide();
            this.statsDom.style.display = "none";
            this.showStats = false;
          }
        }
        break;
      case "KeyZ":
        if (isMacOs ? event.metaKey : event.ctrlKey) {
          if (event.shiftKey) {
            let action = {command: ACTION_TYPE.REDO};
            this.undoRedo(action);
          } else {
            let action = {command: ACTION_TYPE.UNDO};
            this.undoRedo(action);
          }
        }
        break;
      case "KeyC":
        if (isMacOs ? event.metaKey : event.ctrlKey) {
          if (this.selected) this.copyTarget = this.selected;
        }
        break;
      case "KeyV":
        if (isMacOs ? event.metaKey : event.ctrlKey) {
          if (this.copyTarget) this.copyObject(this.copyTarget);
        }
        break;
      case "KeyH":
        if (this.selected) {
          this.toggleVisibility(this.selected, this.selected.layers.mask === 16);
        }
        break;
      default:
        return;
    }
  }

  editorKeyUpHandler_(event) {
    if (document.activeElement.tagName !== "BODY") return;
    switch (event.code) {
      case "ShiftLeft":
        if (this.selected !== undefined) {
          this.dispatchEvent(new CustomEvent(TRANSFORM_TYPE.SNAP_OFF, {}));
        }
        break;
      case isMacOs ? "MetaLeft" : "ControlLeft":
        this.enableMultipleSelect = false;
        break;
      default:
        return;
    }
  }

  selectObject(object) {
    if (this.selected === object) return;
    this.selected = object;
    this.selectionBox.visible = false;
    this.transform.detach();
    this.audioHelper.traverse(child => (child.visible = false));
    if (this.selected !== undefined) {
      this.selected.userData.temporalInput = {
        position: {},
        rotation: {},
        scale: {},
        materialOffset: {},
      };
    }

    let target = "2D";

    if (!this.enableMultipleSelect) this.selectMultipleObject(undefined);

    if (object === undefined) {
      this.selectMultipleObject(undefined);
      if (this.orbit !== undefined) {
        this.orbit.update();
        this.render();
      }
      this.dispatchEvent(
        new CustomEvent(ACTION_TYPE.UNSELECT_OBJECT, {
          detail: {object: undefined},
        }),
      );
      return;
    }

    if (object.isSprite === undefined) {
      if (object === this.sceneEdit || object === this.editCamera) return;
      target = "3D";
      this.box.setFromObject(object);
      if (this.box.isEmpty() === false) {
        this.selectionBox.setFromObject(object);
        this.selectionBox.visible = true;
      }
      if (
        this.transformType === TRANSFORM_TYPE.TRANSLATE ||
        this.transformType === TRANSFORM_TYPE.SCALE ||
        this.transformType === TRANSFORM_TYPE.ROTATE
      ) {
        this.transform.attach(object);
      }
    } else {
      // TODO : Action for sprite select
    }

    this.dispatchEvent(
      new CustomEvent(ACTION_TYPE.SELECT_OBJECT, {
        detail: {object: object, target: target},
      }),
    );
    this.render();
  }

  selectMultipleObject(object, fromUndoRedo = false) {
    if (object === this.sceneEdit || object === this.editCamera) return;
    if (!this.tempGroup) {
      this.tempGroup = new THREE.Group();
      this.tempGroup.name = "tempGroup";
      this.sceneEdit.add(this.tempGroup);
    }

    this.selectionBox.visible = false;
    this.transform.detach();
    this.audioHelper.traverse(child => (child.visible = false));

    let target = "3D";

    if (object === undefined) {
      this.resetSelectionGroup();
      this.orbit.update();
      this.render();

      this.dispatchEvent(
        new CustomEvent(ACTION_TYPE.UNSELECT_OBJECT, {
          detail: {object: undefined},
        }),
      );
      return;
    }

    if (object.isSprite || (this.selected && this.selected.isSprite)) return;

    if (this.tempGroup.children.includes(object)) {
      const tempArray = [...this.tempGroup.children];

      this.resetSelectionGroup();

      this.tempGroup = new THREE.Group();
      this.tempGroup.name = "tempGroup";
      this.sceneEdit.add(this.tempGroup);

      tempArray.forEach(child => {
        if (child !== object) {
          this.tempGroup.attach(child);
        }
      });

      if (this.tempGroup.children.length === 0) {
        this.selectMultipleObject(undefined);
        this.selectObject(object);
        return;
      }

      if (!fromUndoRedo) {
        let obj = {
          type: Command.typeMultiselectRemove,
          target: object,
        };
        let command = new Command(obj);
        this.undo.push(command);
        this.redo.length = 0;
      }
    } else {
      if (this.selected && this.selected !== this.tempGroup) {
        this.tempGroup.attach(this.selected);
      } else {
        const tempArray = [...this.tempGroup.children];

        this.resetSelectionGroup();

        this.tempGroup = new THREE.Group();
        this.tempGroup.name = "tempGroup";
        this.sceneEdit.add(this.tempGroup);

        tempArray.forEach(child => {
          this.tempGroup.attach(child);
        });
      }
      this.tempGroup.attach(object);

      if (!fromUndoRedo) {
        let obj = {
          type: Command.typeMultiselectAdd,
          target: object,
        };
        let command = new Command(obj);
        this.undo.push(command);
        this.redo.length = 0;
      }
    }

    this.box.setFromObject(this.tempGroup);
    this.box.getCenter(this.boxCenter);

    this.tempGroup.position.set(this.boxCenter.x, this.boxCenter.y, this.boxCenter.z);

    this.tempGroup.children.forEach(object => {
      object.position.set(
        object.position.x - (this.boxCenter.x - this.lastBoxCenter.x),
        object.position.y - (this.boxCenter.y - this.lastBoxCenter.y),
        object.position.z - (this.boxCenter.z - this.lastBoxCenter.z),
      );
    });

    this.box.getCenter(this.lastBoxCenter);
    if (this.box.isEmpty() === false) {
      this.selectionBox.setFromObject(this.tempGroup);
      this.selectionBox.visible = true;
    }
    if (
      this.transformType === TRANSFORM_TYPE.TRANSLATE ||
      this.transformType === TRANSFORM_TYPE.SCALE ||
      this.transformType === TRANSFORM_TYPE.ROTATE
    ) {
      this.transform.attach(this.tempGroup);
    }

    this.dispatchEvent(
      new CustomEvent(ACTION_TYPE.SELECT_OBJECT, {
        detail: {object: this.tempGroup, target: target},
      }),
    );
    this.render();
  }

  resetSelectionGroup() {
    if (!this.tempGroup) return;

    while (this.tempGroup.children.length) {
      this.sceneEdit.attach(this.tempGroup.children[0]);
    }

    this.sceneEdit.remove(this.tempGroup);
    this.tempGroup = null;
    this.boxCenter = new THREE.Vector3();
    this.lastBoxCenter = new THREE.Vector3();
  }

  reorderObject(dropInfo, target) {
    const {draggedNodeUuid, dropTargetNodeUuid, droppedLocation} = dropInfo;
    let draggedObject, dropTargetObject;
    if (target === "3D") {
      draggedObject = this.sceneEdit.getObjectByProperty("uuid", draggedNodeUuid);
      dropTargetObject = this.sceneEdit.getObjectByProperty("uuid", dropTargetNodeUuid);
    } else {
      draggedObject = this.sceneGUI.getObjectByProperty("uuid", draggedNodeUuid);
      dropTargetObject = this.sceneGUI.getObjectByProperty("uuid", dropTargetNodeUuid);
    }

    const preOrder = {
      parent: draggedObject.parent,
      index: draggedObject.parent.children.indexOf(draggedObject),
    };

    let prevIdx, idx;
    switch (droppedLocation) {
      case "node":
        if (draggedObject.parent !== dropTargetObject) dropTargetObject.attach(draggedObject);
        break;
      case "siblingGap":
        if (draggedObject.parent !== dropTargetObject.parent) dropTargetObject.parent.attach(draggedObject);
        prevIdx = dropTargetObject.parent.children.indexOf(draggedObject);
        dropTargetObject.parent.children.splice(prevIdx, 1);
        idx = dropTargetObject.parent.children.indexOf(dropTargetObject);
        dropTargetObject.parent.children.splice(idx + 1, 0, draggedObject);
        break;
      case "childrenGap":
        if (draggedObject.parent !== dropTargetObject) dropTargetObject.attach(draggedObject);
        prevIdx = dropTargetObject.children.indexOf(draggedObject);
        dropTargetObject.children.splice(prevIdx, 1);
        dropTargetObject.children.splice(0, 0, draggedObject);
        break;
      default:
        break;
    }

    const postOrder = {
      parent: draggedObject.parent,
      index: draggedObject.parent.children.indexOf(draggedObject),
    };

    let obj = {
      type: Command.typeReorder,
      target: draggedObject,
      preValue: preOrder,
      postValue: postOrder,
    };
    let command = new Command(obj);
    this.undo.push(command);
    this.redo.length = 0;

    if (target === "2D") {
      this.reorderZIndex(this.sceneGUI, 0); // reorder whole sceneGUI for now, perhaps reducing the scope would be necessary for huge sceneGUI
      draggedObject.traverse(sprite => this.updateSpriteTransform(sprite));
    }
    this.render();
  }

  reorderZIndex(rootNode, depth) {
    if (depth === undefined) {
      depth = 0;
      let node = rootNode;
      while (node.parent !== null) {
        node = node.parent;
        depth++;
      }
    }
    let count = 1;
    rootNode.children.forEach(child => {
      if (!child.isSprite) return;
      child.position.z = count * 100 ** -depth;
      count++;
      if (child.children.length) {
        this.reorderZIndex(child, depth + 1);
      }
    });
  }

  groupObject() {
    if (!this.tempGroup) return;
    if (this.tempGroup.children.length > 1) {
      this.tempGroup.children.forEach(child => {
        delete child.userData.isSprite;
        delete child.userData.canScript;
      });
      this.tempGroup.name = "Group";
      this.tempGroup.title = this.tempGroup.name + "(" + this.tempGroup.uuid.substring(0, 3) + ")";
      this.tempGroup.userData.isSprite = true;
      this.tempGroup.userData.friction = 1;
      this.tempGroup.userData.mass = 20;
      this.tempGroup.userData.canCollide = true;
      this.tempGroup.userData.isKinematic = true;
      this.tempGroup.userData.opacity = 1;
      this.tempGroup.userData.type = "GroupedObject";
      this.objects.push(this.tempGroup);
      this.tempGroup = null;
    }
  }

  changeTransform(transform) {
    if (transform === TRANSFORM_TYPE.SELECT) {
      this.transform.detach();
      this.transformType = TRANSFORM_TYPE.SELECT;
    } else if (transform === TRANSFORM_TYPE.MULTI) {
      this.transform.detach();
      this.transformType = TRANSFORM_TYPE.MULTI;
    } else {
      if (this.selected !== undefined) {
        this.transform.attach(this.selected);
      }
      this.transform.setMode(transform);
      this.transformType = transform;
    }
    this.render();
  }

  changeSnapMode(snap) {
    const {isActivated, snapProperty} = snap;

    if (isActivated) {
      this.transform.setTranslationSnap(snapProperty.translateSnap);
      this.transform.setRotationSnap(THREE.MathUtils.degToRad(snapProperty.rotateSnap));
      this.transform.setScaleSnap(snapProperty.scaleSnap);
    } else {
      this.transform.setTranslationSnap(null);
      this.transform.setRotationSnap(null);
      this.transform.setScaleSnap(null);
    }
  }

  changePublishMode(mode) {
    if (!this.sceneEdit) return;
    this.sceneEdit.userData.publishMode = mode;
  }

  drawPath(positionQueue) {
    const vectorPoints = [];

    for (let i = 0; i < positionQueue.length; i++) {
      vectorPoints.push(new THREE.Vector3(positionQueue[i].x, positionQueue[i].y, positionQueue[i].z));
      const geometry = new THREE.SphereGeometry(0.2, 4, 2);
      const material = new THREE.MeshBasicMaterial({color: 0xff0000});
      const sphere = new THREE.Mesh(geometry, material);
      sphere.position.set(positionQueue[i].x, positionQueue[i].y, positionQueue[i].z);
      this.scenePlay.add(sphere);
    }

    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0xff0000,
      linewidth: 10,
    });
    const lineGeometry = new THREE.BufferGeometry().setFromPoints(vectorPoints);
    const line = new THREE.Line(lineGeometry, lineMaterial);

    this.scenePlay.add(line);
  }

  async changeMode(mode) {
    this.orbit.keyActive = {LEFT: false, UP: false, RIGHT: false, BOTTOM: false, FORWARD: false, SHIFT: false, BACKWARD: false};
    if (mode === MODE_TYPE.EDIT_MODE) {
      document.body.setAttribute("style", `
        background-color: none;
        background-image: none;
      `);
      document.querySelector("button#VRButton")?.remove();
      if (this.tutorial) this.tutorial.onPlayEnd();
      // [TEMP] clear all intervals & timeouts in browser when change play-mode to edit-mode
      const timerLastIdx = setInterval(() => {});
      for (let i = timerLastIdx; i > 0; i--) {
        clearInterval(i);
        clearTimeout(i);
      }

      if (window.location.hostname !== "localhost") {
        setInterval(() => {
          const monitorId = localStorage.getItem("monitorId");

          if (monitorId) {
            updateWorldMonitoring({id: monitorId}, {lastResponsedAt: Date.now()});
          }
        }, 60000);
      }

      if (this.physics) delete this.physics;
      if (this.scenePlay) {
        this.scenePlay.traverse(child => {
          if (child.type === "Audio") {
            if (child.isPlaying === true) child.stop();
          }
        });
        delete this.scenePlay;
      }

      if (this.scenePlayGUI) {
        this.showOriginalCSS2DObjects(this.sceneGUI, this.scenePlayGUI);
        delete this.scenePlayGUI;
      }
      if (this.playCamera) delete this.playCamera;
      if (this.scenePlayCameras) delete this.scenePlayCameras;
      if (this.rayLine) delete this.rayLine;
      if (this.scriptLauncher) {
        this.scriptLauncher.dispose();
        delete this.scriptLauncher;
        TWEEN.removeAll();
        this.sceneEdit.traverse(obj => delete obj.userData.playInfo);
      }
      if (this.playControls) this.playControls.dispose();
      if (this.tutorial) this.tutorial.reset(true);
      if (this.avatar) {
        this.avatar.dispose();
        delete this.avatar;
      }

      if (this.isSilhouetteReady) {
        this.isSilhouetteReady = false;
        delete this.avatarSilhouette;
      }
      if (this.isOutlineReady) {
        this.isOutlineReady = false;
        this.renderer.removeOutline();
      }

      this.renderer.glRenderer.info.autoReset = false;
      if (this.joystickAxis) this.joystickAxis.onMove(event => {});
      this.joystick.hide();
      this.joystick.removeJoystickGuide();
      this.keyDownCallbacks = {};
      this.keyUpCallbacks = {};
      this.particleSystemArray = [];
      this.mode = mode;

      this.showSceneHelpers = true;
      this.transform.enabled = true;
      this.orbit.enabled = true;
      this.isAvatarReady = false;
      this.isSetupCompleted = false;

      this.world.activateGizmo();

      const childElements = this.canvas.children;
      for (let i = childElements.length - 1; i >= 0; i--) {
        const ele = childElements[i];
        if (ele.tagName === "VIDEO") this.canvas.removeChild(ele);
      }

      this.renderer.updatePostProcess(this.sceneEdit, this.editCamera);
      if (this.sceneEdit.userData.toneMapping.mode !== undefined) {
        this.renderer.glRenderer.toneMapping = this.sceneEdit.userData.toneMapping.mode;
        this.renderer.glRenderer.toneMappingExposure = this.sceneEdit.userData.toneMapping.exposure;
      }

      this.labelRenderer.domElement.addEventListener("mousedown", this.onMouseDown, false);

      document.addEventListener("keydown", this.editorKeyDownHandler);
      document.removeEventListener("keydown", this.playKeyDownHandler);
      document.removeEventListener("keyup", this.playKeyUpHandler);

      this.guiCamera.layers.disable(4);
      this.rayCaster.layers.disable(4);

      this.dragControl.activate();
      this.animate = () => null;
      delete this.animationFrameCallbacks.playMode;
      this.sceneGUI.traverse(sprite => this.updateSpriteTransform(sprite));
      this.render();
    } else if (mode === MODE_TYPE.PLAY_MODE) {
      const mobileDevice = window.matchMedia("(max-width: 767px) and (max-height: 767px)");
      document.body.setAttribute("style", `
        background-color: #1c1c1c;
        background-image: url(${loadingAnimation});
        background-size: ${(mobileDevice.matches) ? "668px 376px" : "964px 542.25px"};
        background-position: center;
        z-index: -10;
      `);
      this.moveDirection = {left: 0, right: 0, forward: 0, back: 0, jump: 0};

      this.showSceneHelpers = false;
      this.transform.enabled = false;
      this.orbit.enabled = false;
      this.world.deactivateGizmo();
      this.renderer.glRenderer.info.autoReset = true;
      this.dispatchEvent(new CustomEvent(ACTION_TYPE.SET_CODE_EDITOR, {}));

      // this.canvas.removeEventListener("mousedown", this.onMouseDown);
      document.removeEventListener("keydown", this.editorKeyDownHandler);
      document.addEventListener("keydown", this.playKeyDownHandler);
      document.addEventListener("keyup", this.playKeyUpHandler);
      this.dragControl.deactivate();
      this.animate = this.animate_.bind(this);

      this.scenePlayCameras = [];
      if (this.versionManager.checkCompatibility.avatar.isCreatedByDefault()) {
        this.playCamera = this.createPlayCamera();
      }

      this.playCameraRaycaster.camera = this.playCamera;

      this.guiCamera.layers.enable(4);
      this.rayCaster.layers.enable(4);

      if (this.isTouchDevice) {
        this.addJoystick(this.joystick.isDynamic);
        this.joystick.show();
      }

      if (!this.gameServer) {
        if (this.versionManager.checkCompatibility.script.isOOP()) this.gameServer = new GameServerV1(this);
        else this.gameServer = new GameServer(this);
      }
      // Create a play scene
      if (this.runMode === "play") {
        this.mode = mode;

        // PUBLISHED
        this.scriptLauncher = this.scriptManager.getScriptLauncher(this.versionManager.checkCompatibility.script.isOOP());
        if (this.playCamera) this.renderer.updatePostProcess(this.scenePlay, this.playCamera);
        if (this.scenePlay.userData.toneMapping.mode !== undefined) {
          this.renderer.glRenderer.toneMapping = this.scenePlay.userData.toneMapping.mode;
          this.renderer.glRenderer.toneMappingExposure = this.scenePlay.userData.toneMapping.exposure;
        }
        // Before the avatar enters the children of the scene.
        this.scenePlayObjects = this.scenePlay.children.slice();

        await this.setupParticleSystem();

        if (this.versionManager.checkCompatibility.script.isOOP()) {
          await _setupPhysicsRevise(this);
        } else {
          await _setupPhysics(this);
        }
        await this.setupAudios();
        this.playVideoObject(this.scenePlay, true);
        if (this.scenePlay.userData.publishMode === undefined) {
          this.scenePlay.userData.publishMode = MODE_TYPE.SINGLEPLAY_MODE;
        }

        this.scenePlay.traverse(obj => {
          if (obj.isCamera) {
            const renderer = new Renderer(this.canvas);
            renderer.updatePostProcess(this.scenePlay, obj);
            obj.renderer = renderer;
            this.scenePlayCameras.push(obj);
            if (obj.isPerspectiveCamera) {
              obj.aspect = this.canvas.clientWidth / this.canvas.clientHeight;
              obj.updateProjectionMatrix();
            }
          }
        });
        this.updateCameraOrder();

        // For Gstar UGC Contents
        if (
          (this.scenePlay.userData.gStar && this.scenePlay.userData.gStar.maxClients === 1) ||
          this.scenePlay.userData.publishMode === MODE_TYPE.SINGLEPLAY_MODE
        ) {
          // temporal code for multi select bug
          const obj = this.scenePlay.getObjectByName("tempGroup");

          if (obj !== undefined) {
            while (obj.children.length) {
              this.scenePlay.attach(obj.children[0]);
            }
            obj.removeFromParent();
          }
          await this.runScript();
        } else {
          //check if it block code
          if (this.codeEditorMode === EDITORMODE.BLOCK) {
            await this.gameServer.joinOrCreateRoom(this);
            //check game server status
            if (!this.gameServer.serverStatus) {
              const obj = this.scenePlay.getObjectByName("tempGroup");

              if (obj !== undefined) {
                while (obj.children.length) {
                  this.scenePlay.attach(obj.children[0]);
                }
                obj.removeFromParent();
              }
              await this.runScript();
            } else {
              await this.scriptLauncher.init();
              if (this.versionManager.checkCompatibility.script.isOOP()) {
                await this.scriptLauncher.generateUserScript();
                await this.scriptLauncher.runUserScript();
              }
            }
          } else {
            await this.scriptLauncher.init();
            await this.gameServer.joinOrCreateRoom(this);

            if (this.versionManager.checkCompatibility.script.isOOP()) {
              await this.scriptLauncher.generateUserScript();
              await this.scriptLauncher.runUserScript();
            }
          }
        }
        setTimeout(() => {
          this.dispatchEvent(new CustomEvent("LOADING_END", {detail: {object: undefined}}));
        }, 1000);
        if (this.pId === REDBRICK_EVENT_PID.RUNNERZ) {
          setInterval(() => {
            if (!this.avatar) return;
            let {x, y, z} = this.avatar.model.position;
            x = parseFloat(x.toFixed(1));
            y = parseFloat(y.toFixed(1));
            z = parseFloat(z.toFixed(1));
            this.avatarPositionQueue.push(x, y, z);
            if (this.avatarPositionQueue.length > 75) {
              this.avatarPositionQueue.splice(0, 3);
            }

            this.dispatchEvent(
              new CustomEvent("UPDATE_PLAYDATA", {
                detail: {
                  playData: this.avatarPositionQueue,
                },
              }),
            );
          }, 1000);
        }
        // requestAnimationFrame(this.animate);
        this.animationFrameCallbacks.playMode = this.animate;
        // IPX NEWYORK
        if (this.pId === IPX_PID.NEWYORK) {
          this.setNpcs(npcSettings);
        } else if (this.pId === IPX_PID.DONUT_DRIVE || this.pId === IPX_PID.DONUT_DRIVE_TEST) {
          this.setNpcs(donutNpcSettings);
        }
      } else {
        new THREE.ObjectLoader().parse(this.sceneEdit.toJSON(), async scene => {
          this.scenePlay = scene;
          this.scenePlayObjects = this.scenePlay.children.slice();

          this.mode = mode;

          if (this.playCamera) this.renderer.updatePostProcess(this.scenePlay, this.playCamera);
          if (this.scenePlay.userData.toneMapping.mode !== undefined) {
            this.renderer.glRenderer.toneMapping = this.scenePlay.userData.toneMapping.mode;
            this.renderer.glRenderer.toneMappingExposure = this.scenePlay.userData.toneMapping.exposure;
          }
          this.playVideoObject(this.scenePlay, false);

          this.scenePlay.traverse(obj => {
            if (obj.isCamera) {
              const renderer = new Renderer(this.canvas);
              renderer.updatePostProcess(this.scenePlay, obj);
              obj.renderer = renderer;
              this.scenePlayCameras.push(obj);
              if (obj.isPerspectiveCamera) {
                obj.aspect = this.canvas.clientWidth / this.canvas.clientHeight;
                obj.updateProjectionMatrix();
              }
            }
          });
          this.updateCameraOrder();

          // temporal code for multi select bug
          const obj = scene.getObjectByName("tempGroup");

          if (obj !== undefined) {
            while (obj.children.length) {
              this.scenePlay.attach(obj.children[0]);
            }
            obj.removeFromParent();
          }
          await this.setupParticleSystem();
          // PLAY BUTTON
          if (this.versionManager.checkCompatibility.script.isOOP()) {
            await _setupPhysicsRevise(this);
          } else {
            await _setupPhysics(this);
          }
          await this.setupAudios();
          await this.runScript();
          // requestAnimationFrame(this.animate);
          this.animationFrameCallbacks.playMode = this.animate;

          if (this.tutorial) this.tutorial.onPlayStart();
        });

        if (this.codeEditorMode !== EDITORMODE.JAVASCRIPT_3D) {
          this.scenePlayGUI = new THREE.ObjectLoader().parse(this.sceneGUI.toJSON());
          if (this.codeEditorMode !== EDITORMODE.JAVASCRIPT_3D) {
            this.cloneCSS2DObjects(this.sceneGUI, this.scenePlayGUI);
          }
        }
      }
    }
  }



  cloneCSS2DObjects(source, destination) {
    source.traverse(node => {
      if (node instanceof CSS2DObject) {
        // Clone the CSS2DObject with visibility changes
        let originalElement = node.element;
        let cloneElement = originalElement.cloneNode(true);

        // Hide the text of the original element
        originalElement.style.display = "none";

        // Ensure the cloned element is visible (if needed)
        cloneElement.style.display = "";

        let clone = new CSS2DObject(cloneElement);
        clone.position.copy(node.position);
        clone.rotation.copy(node.rotation);
        clone.scale.copy(node.scale);

        // Find the corresponding parent object in the destination scene
        destination.traverse(destNode => {
          if (destNode.uuid === node.parent.uuid) {
            destNode.add(clone);
          }
        });
      }
    });
  }
  showOriginalCSS2DObjects(originalScene, clonedScene) {
    originalScene.children.forEach(child => {
      if (child instanceof CSS2DObject) {
        child.element.style.display = "";
      }
    });

    clonedScene?.traverse(node => {
      if (node instanceof CSS2DObject) {
        // Check if the node's element and its parent exist before trying to remove it from the DOM
        if (node.element && node.element.parentNode) {
          node.element.parentNode.removeChild(node.element);
        }
        // Safely remove the node from its Three.js parent
        if (node.parent) {
          node.parent.remove(node);
        }
      }
    });
  }

  createPlayCamera() {
    const playCamera = new THREE.PerspectiveCamera(50, this.canvas.clientWidth / window.innerHeight, 1, 1000);
    playCamera.changeShootingMode = this.changeShootingMode.bind(this);
    playCamera.position.set(0, 6, -15);
    playCamera.lookAt(0, 5, 0);
    playCamera.layers.enableAll();
    playCamera.userData.enabled = true;
    playCamera.userData.depth = 0;
    if (this.scenePlay) this.renderer.updatePostProcess(this.scenePlay, playCamera);
    playCamera.renderer = this.renderer;
    this.scenePlayCameras.push(playCamera);
    return playCamera;
  }

  createPlayControls(camera) {
    this.playControls = new PlayControls(this, camera);
  }

  playVideoObject(scene, isMutedByForce = true) {
    scene.traverse(obj => {
      if (obj.userData.type === "video") {
        const videoElement = document.createElement("video");
        this.canvas.appendChild(videoElement);
        // Need to remove after asset unification
        if (this.pId === PID.MALAYASIA) {
          videoElement.src = "https://d2f4dt8igm02oj.cloudfront.net/redbrick-asset/video/choux-test.mp4";
        } else {
          videoElement.src = obj.userData.src;
        }
        videoElement.autoplay = obj.userData.isVideoAutoPlay;
        videoElement.loop = true;
        videoElement.muted = isMutedByForce || obj.userData.isVideoMute;
        videoElement.controls = true;
        videoElement.playsInline = true;
        videoElement.crossOrigin = "anonymous";
        this.videoElements[obj.uuid] = videoElement;
        videoElement.play();

        const videoTexture = new THREE.VideoTexture(videoElement);
        videoTexture.encoding = THREE.sRGBEncoding;

        const material = new THREE.MeshBasicMaterial({
          map: videoTexture,
        });
        material.side = obj.userData.isDoubleSide ? THREE.DoubleSide : THREE.FrontSide;

        obj.material = material;
        obj.userData.isKinematic = true;
      }
    });
  }

  dragstartSprite_(event) {
    this.orbit.enabled = false;
    this.selectObject(event.object);

    if (this.transformDraggingStart) {
      this.transformDraggingStart = false;
      this.transformStartValue = {
        position: this.selected.position.clone(),
        scale: this.selected.scale.clone(),
      };
    }
  }

  dragendSprite_(event) {
    this.orbit.enabled = true;

    if (!this.transformDraggingStart) {
      this.selected.userData.positionX = this.selected.position.x;
      this.selected.userData.positionY = this.selected.position.y;
      this.selected.userData.scaleX = this.selected.scale.x;
      this.selected.userData.scaleY = this.selected.scale.y;
      this.updateSpriteTransformFromScene(this.selected);

      this.dispatchEvent(
        new CustomEvent(ACTION_TYPE.CHANGE_PROPERTY_FROM_SCENE, {
          detail: {object: this.selected},
        }),
      );

      this.transformDraggingStart = true;
      let transformEndValue = {
        position: this.selected.position.clone(),
        scale: this.selected.scale.clone(),
      };

      let obj = {
        type: Command.typeSpriteTransform,
        target: this.selected,
        preValue: this.transformStartValue,
        postValue: transformEndValue,
      };
      let command = new Command(obj);
      this.undo.push(command);
      this.redo.length = 0;
    } else {
    }
  }

  turnBackgroundMode(bool) {
    window.isBackgroundMode = bool;

    if (bool === true) {
      this.audioListener.gain.gain.value = 0;
    } else {
      this.audioListener.gain.gain.value = 1;
    }
  }

  setupAudios() {
    return new Promise((resolve, reject) => {
      const loader = new THREE.AudioLoader();
      loader.setCrossOrigin("anonymous");

      let audioCount = 0;

      this.scenePlay.traverse(obj => {
        if (obj.userData.type === "audio" || obj.userData.hasAudio) {
          const {path, volume, playbackRate, offset, duration, loop, positional, autoplay} = obj.userData.audioInfo;
          if (!path) {
            return;
          }
          let audio;
          if (positional.isActive) {
            const {distanceModel, refDistance, maxDistance, rolloffFactor, directionalCone} = positional;
            audio = new THREE.PositionalAudio(this.audioListener);
            audio.setDistanceModel(distanceModel);
            audio.setRefDistance(refDistance);
            audio.setMaxDistance(maxDistance);
            audio.setRolloffFactor(rolloffFactor);
            if (directionalCone.isActive) {
              audio.setDirectionalCone(directionalCone.coneInnerAngle, directionalCone.coneOuterAngle, directionalCone.coneOuterGain);
            }
          } else {
            audio = new THREE.Audio(this.audioListener);
          }
          obj.add(audio);

          ++audioCount;
          loader.load(
            path,
            buffer => {
              audio.setBuffer(buffer);
              audio.setVolume(volume);
              audio.setPlaybackRate(playbackRate);
              if (offset.isActive) audio.offset = offset.value;
              if (duration.isActive) audio.duration = duration.value;
              if (loop.isActive) {
                audio.setLoop(true);
                if (loop.loopStart.isActive) audio.setLoopStart(loop.loopStart.value);
                if (loop.loopEnd.isActive) audio.setLoopEnd(loop.loopEnd.value);
              }
              if (window.isBackgroundMode === false) {
                // for previous projects
                if (autoplay === undefined || autoplay === true) {
                  audio.play();
                }
              }
              --audioCount;
              if (audioCount === 0) {
                resolve("Audio objects loaded");
              }
            },
            xhr => {},
            err => {
              reject("Error occurs in audio loading process");
            },
          );
        }
      });
      if (audioCount === 0) {
        resolve("No audio object in play scene");
      }
    });
  }

  setupParticleSystem() {
    return new Promise((resolve, reject) => {
      let particleSystemCount = 0;
      this.scenePlay.traverse(obj => {
        if (obj.userData.type === "particleSystem") {
          ++particleSystemCount;

          System.fromJSONAsync(obj.userData.system, THREE)
            .then(loaded => {
              obj.children = [];

              const particleRenderer = new SpriteRenderer(obj, THREE);
              const system = loaded.addRenderer(particleRenderer);

              this.particleSystemArray.push(system);
              obj.emitterId = system.emitters[0].id;
              --particleSystemCount;

              if (particleSystemCount === 0) {
                resolve("Particle system loaded");
              }
            })
            .catch(err => {
              reject("Error occurs in particle system loading process");
            });
        }
      });
      if (particleSystemCount === 0) {
        resolve("No particle system in play scene");
      }
    });
  }

  setupEditModeParticleSystem() {
    this.sceneEdit.traverse(obj => {
      if (obj.userData.type === "particleSystem") {
        // copy required values
        const particleSystem = new CustomParticleSystem();
        particleSystem.uuid = obj.uuid;
        particleSystem.name = obj.name;
        if (obj.userData.title) {
          particleSystem.title = obj.userData.title;
          particleSystem.userData.title = obj.userData.title;
        } else {
          particleSystem.title = `ParticleSystem(${particleSystem.uuid.substring(0, 3)})`;
        }
        particleSystem.userData.system = obj.userData.system;
        particleSystem.userData.canScript = obj.userData.canScript;
        particleSystem.position.set(obj.position.x, obj.position.y, obj.position.z);
        particleSystem.rotation.set(obj.rotation.x, obj.rotation.y, obj.rotation.z);
        particleSystem.scale.set(obj.scale.x, obj.scale.y, obj.scale.z);

        // set title to delete previous particle-object3d
        obj.title = "particleObject3d";

        // create system and set system to edit mode
        particleSystem.createSystem();
        this.editParticleSystemArray.push(particleSystem.system);
        particleSystem.updateParametersFromUserData(0, obj.userData);
        particleSystem.updateProperties();
        particleSystem.resetEmitter(0);

        // add new particle to scene
        if (obj.parent.type === "Scene") {
          this.objects.push(particleSystem);
        }
        obj.parent.add(particleSystem);
      }
    });
    // remove previous particle-object3d from scene
    while (this.sceneEdit.getObjectByProperty("title", "particleObject3d") !== undefined) {
      const particleObject3d = this.sceneEdit.getObjectByProperty("title", "particleObject3d");
      particleObject3d.parent.remove(particleObject3d);
    }
  }

  getMousePosition(dom, x, y) {
    var rect = dom.getBoundingClientRect();
    return [(x - rect.left) / rect.width, (y - rect.top) / rect.height];
  }

  getIntersects(point, is3D) {
    let mouse = new THREE.Vector2();
    mouse.set(point.x * 2 - 1, -(point.y * 2) + 1);

    // Playmode raycast for click event
    if (this.mode === MODE_TYPE.PLAY_MODE) {
      if (is3D) {
        let intersects = [];
        for (let i = this.scenePlayCameras.length - 1; i >= 0; i--) {
          const camera = this.scenePlayCameras[i];
          if (!camera.enabled) continue;
          this.rayCaster.setFromCamera(mouse, camera);
          intersects = this.rayCaster
            .intersectObjects(this.scenePlay.children)
            .filter(intersect => intersect.object.userData.playInfo && intersect.object.userData.playInfo.isAlive === true);
          if (intersects.length > 0) break;
        }
        return intersects;
      } else {
        this.rayCaster.setFromCamera(mouse, this.guiCamera);
        return this.rayCaster.intersectObjects(this.objects2D).filter(intersect => intersect.object.visible === true);
      }
    } else if (this.mode === MODE_TYPE.EDIT_MODE) {
      if (is3D) {
        this.rayCaster.setFromCamera(mouse, this.editCamera);
        return this.rayCaster.intersectObjects(this.objects).filter(intersect => intersect.object.visible === true);
      } else {
        this.rayCaster.setFromCamera(mouse, this.guiCamera);
        return this.rayCaster.intersectObjects(this.objects2D);
      }
    }
  }

  getParentGroup(object) {
    if (object.parent.type === "Scene") return object;
    return this.getParentGroup(object.parent);
  }

  executeOnclickFunction(object) {
    if (object.parent === null) return;
    if (Array.isArray(object.onClickObject) && object.onClickObject.length) {
      const player = this.scriptLauncher.getPlayer();
      object.onClickObject.forEach(callback => {
        try {
          callback(player);
        } catch (error) {
          console.error(`${error.name} occurred in the onClick function: ${error.message}`);
          window.alert(`${error.name} occurred in the onClick function: ${error.message}`);
        }
      });
    } else this.executeOnclickFunction(object.parent);
  }

  handleClick() {
    // Playmode click event
    if (this.mode === MODE_TYPE.PLAY_MODE) {
      if (this.onDownPosition.distanceTo(this.onUpPosition) === 0) {
        let intersects2D = this.getIntersects(this.onUpPosition, false);

        if (intersects2D.length > 0) {
          let object = intersects2D[0].object;

          if (Array.isArray(object.onClickSprite) && object.onClickSprite.length) {
            const player = this.scriptLauncher.getPlayer();
            object.onClickSprite.forEach(callback => {
              try {
                callback(player);
              } catch (error) {
                console.error(`${error.name} occurred in the onClick function: ${error.message}`);
                window.alert(`${error.name} occurred in the onClick function: ${error.message}`);
              }
            });
          }
          if (this.gameServer.room) {
            this.gameServer.sendInputToServer("mouse", {target: object.title});
          }
          return;
        }

        let intersects3D = this.getIntersects(this.onUpPosition, true);
        if (intersects3D.length > 0) {
          let object = intersects3D[0].object;

          this.executeOnclickFunction(object);
          if (this.gameServer.room) {
            this.gameServer.sendInputToServer("mouse", {target: object.title});
          }
        } else {
          if (!this.gameServer.room) {
            if (this.onClickEmpty !== undefined) this.onClickEmpty(this.scriptLauncher.getPlayer());
          } else {
            this.gameServer.sendInputToServer("mouse", {target: ""});
          }
        }
      }
    } else if (this.mode === MODE_TYPE.EDIT_MODE) {
      // for multiple select
      if (this.enableMultipleSelect) {
        if (this.onDownPosition.distanceTo(this.onUpPosition) === 0) {
          let intersects2D = this.getIntersects(this.onUpPosition, false);

          if (intersects2D.length > 0) {
            // TODO - GUI mutiple select
            // let object = intersects2D[0].object;
            // this.selectMultipletObject(object);
            return;
          }

          let intersects3D = this.getIntersects(this.onUpPosition, true);

          if (intersects3D.length > 0) {
            let object = intersects3D[0].object;

            if (object.userData.object !== undefined) {
              // this is helper object
              this.selectMultipleObject(object.userData.object);
            } else {
              if (object.userData !== undefined) {
                while ((object.userData.isSprite === undefined || object.userData.isSprite === false) && object.parent !== undefined) {
                  object = object.parent;
                }
              }
              this.selectMultipleObject(object);
            }
          } else {
            this.selectMultipleObject(undefined);
          }
          this.render();
        }
      } else {
        if (this.onDownPosition.distanceTo(this.onUpPosition) === 0) {
          let intersects2D = this.getIntersects(this.onUpPosition, false);

          if (intersects2D.length > 0) {
            let object = intersects2D[0].object;
            this.selectObject(object);
            return;
          }

          let intersects3D = this.getIntersects(this.onUpPosition, true);

          if (intersects3D.length > 0) {
            let object = intersects3D[0].object;

            if (object.parent.type === "Object3D") {
              if (object.name === "picker") {
                this.selectObject(object.userData.object);
              } else {
                this.selectObject(this.getParentGroup(object));
              }
            } else {
              if (object.userData.object !== undefined) {
                // this is helper object
                this.selectObject(object.userData.object);
              } else {
                if (object.userData !== undefined) {
                  while ((object.userData.isSprite === undefined || object.userData.isSprite === false) && object.parent !== undefined) {
                    object = object.parent;
                  }
                }
                this.selectObject(object);
              }
            }
          } else {
            this.selectObject(undefined);
          }
          this.render();
        }
      }
    }
  }

  onMouseDown_(event) {
    if (!this.mousedownFlag) {
      this.mousedownFlag = true;
      const listener = new THREE.AudioListener();
      const audio = new THREE.Audio(listener);
      audio.play();
    }
    var array = this.getMousePosition(this.canvas, event.clientX, event.clientY);
    this.onDownPosition.fromArray(array);
    this.isClicked = true;
    this.labelRenderer.domElement.addEventListener("mouseup", this.onMouseUp, false);
  }

  onMouseUp_(event) {
    var array = this.getMousePosition(this.canvas, event.clientX, event.clientY);
    this.onUpPosition.fromArray(array);
    this.isClicked = false;

    this.handleClick();

    this.labelRenderer.domElement.removeEventListener("mouseup", this.onMouseUp, false);
  }

  onMouseMove_(event) {
    var array = this.getMousePosition(this.canvas, event.clientX, event.clientY);

    this.cursorPosition.fromArray(array);
  }

  onDrop_(e) {
    const assetInfo = JSON.parse(e.dataTransfer.getData("text/plain"));
    const {name, url, topCategory, midCategory, subCategory, duration} = assetInfo;

    e.preventDefault();

    if (
      this.orbit.keyActive.LEFT ||
      this.orbit.keyActive.UP ||
      this.orbit.keyActive.RIGHT ||
      this.orbit.keyActive.BOTTOM ||
      this.orbit.keyActive.FORWARD ||
      this.orbit.keyActive.SHIFT ||
      this.orbit.keyActive.BACKWARD
    ) {
      this.orbit.keyActive = {LEFT: false, UP: false, RIGHT: false, BOTTOM: false, FORWARD: false, SHIFT: false, BACKWARD: false};
    }

    switch (topCategory) {
      case ASSET_TYPE.VIDEO:
        this.addVideo(name, url);
        break;
      case ASSET_TYPE.SOUND:
        const sound = {
          duration: duration || 0,
          name: name,
          path: url,
        };
        this.addAudio(name, sound);
        break;
      case ASSET_TYPE.IMAGE:
        switch (midCategory) {
          case ASSET_TYPE.GUI:
            this.addGui(name, url);
            break;
          case "TEXTURE":
            switch (subCategory) {
              case "PARTICLEMAP":
                this.addParticleSystem(url);
                break;
              default:
                break;
            }
            break;
          default:
            this.addImage(name, url);
            break;
        }
        break;
      case ASSET_TYPE.MODEL:
        switch (midCategory) {
          case "ASSET":
          case ASSET_TYPE.AD_MODEL:
            this.addDemoModel(name, url);
            break;
          case ASSET_TYPE.LIGHT:
            this.addLight(name.toUpperCase());
            break;
          case ASSET_TYPE.MESH:
            this.addMesh("default", name.toUpperCase());
            break;
          case ASSET_TYPE.SPAWN:
            switch (name) {
              case ASSET_TYPE.STARTING_POINT:
                this.addStartLocation(url);
                break;
              case ASSET_TYPE.PORTAL:
                this.addPortal();
                break;
              default:
                break;
            }
            break;
          default:
            this.addDemoModel(name, url);
            break;
        }
        break;
      default:
        break;
    }
    sessionStorage.setItem("dragging_asset", "");
  }

  onDoubleClick_(event) {}

  getChatMode(chatMode) {
    this.chatMode = chatMode;
  }

  makeSpeechBubble = ({type, name, message}) => {
    if (!this.avatar) return;

    try {
      let _msg = message;
      if (_msg.length > CHAT_ONE_LINE_CHAR_LIMIT) {
        if (_msg.length > 30) {
          _msg = _msg.substring(0, CHAT_ONE_LINE_CHAR_LIMIT) + "\n" + _msg.substring(CHAT_ONE_LINE_CHAR_LIMIT, 29) + "...";
        } else {
          _msg = _msg.substring(0, CHAT_ONE_LINE_CHAR_LIMIT) + "\n" + _msg.substring(CHAT_ONE_LINE_CHAR_LIMIT, _msg.length);
        }
      }
      if (type === CHAT_TYPE.MESSAGE) {
        if (this.userInfo.name === name) {
          const isResetBubble = this.avatar.model.speechBubble;
          this.showSpeechBubble(isResetBubble, _msg, this.avatar.model);
        } else {
          for (const [key, value] of Object.entries(this.playerStates)) {
            if (key.includes("avatar")) {
              if (value.clientInfo.userName === name) {
                const isResetBubble = this.syncObjects[key].speechBubble;
                this.showSpeechBubble(isResetBubble, _msg, this.syncObjects[key]);
              }
            }
          }
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  showSpeechBubble(isResetBubble, message, model) {
    const scenePlay = this.scenePlay;
    const playCamera = this.avatar.followingCamera;
    if (!playCamera) return;
    const {x, y, z} = model.position;

    if (isResetBubble) {
      const [_bubbleBox, _speechBubble] = model.speechBubble;
      scenePlay.remove(_bubbleBox);
      scenePlay.remove(_speechBubble);
    }

    const geometry = new THREE.PlaneGeometry(message.length / 3 > 3.2 ? 3.2 : message.length / 3, 0.8);
    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
    });
    const bubbleBox = new THREE.Mesh(geometry, material);
    const box = new THREE.Box3();

    box.setFromObject(model);
    bubbleBox.position.set(x, this.box.max.y - this.box.min.y + y + 1.2, z);
    bubbleBox.rotation.y = Math.atan2(playCamera.position.x - bubbleBox.position.x, playCamera.position.z - bubbleBox.position.z);

    const fontLoader = new FontLoader();
    const font = `${URL.S3_FONT}/Pretendard_Medium_Regular.json`;

    fontLoader.load(font, function (font) {
      const color = 0x000000;
      const matDark = new THREE.LineBasicMaterial({
        color: color,
        side: THREE.DoubleSide,
      });

      const geo = new TextGeometry(message, {
        font,
        size: 0.15,
        height: 0,
      });

      geo.computeBoundingBox();
      const xMid = -0.5 * (geo.boundingBox.max.x - geo.boundingBox.min.x);
      geo.translate(xMid, 0, 0);

      const speechBubble = new THREE.Mesh(geo, matDark);
      speechBubble.position.set(
        x,
        message.length > CHAT_ONE_LINE_CHAR_LIMIT ? y + 4.7 : y + 4.5,
        playCamera.position.z - bubbleBox.position.z > 0 ? bubbleBox.position.z + 0.01 : bubbleBox.position.z - 0.01,
      );
      speechBubble.rotation.y = Math.atan2(
        playCamera.position.x - speechBubble.position.x,
        playCamera.position.z - speechBubble.position.z,
      );
      scenePlay.add(bubbleBox);
      scenePlay.add(speechBubble);
      model.speechBubble = [bubbleBox, speechBubble, message];

      setTimeout(() => {
        scenePlay.remove(bubbleBox);
        scenePlay.remove(speechBubble);
      }, 3000);
    });
  }

  getLandMode(playOption) {
    if (this.runMode !== "play") return;
    switch (playOption) {
      case "play":
      case "social":
      case "avatar":
      case "studio":
        this.landMode = true;
        break;
      default:
        this.landMode = false;
        break;
    }
  }

  //VR squeeze handlers
  onVRControllerLeftSqueezeStart_() {
    if (this.avatar.model !== undefined && this.isAvatarControllerActive) {
      this.moveDirection.jump = 1;
      this.setEmoteAnimation(undefined);
    }
  }
  onVRControllerLeftSqueezeEnd_() {
    this.moveDirection.jump = 0;
  }

  onVRControllerRightSqueezeStart_() {
    if (this.avatar.model !== undefined && this.isAvatarControllerActive) {
      this.moveDirection.jump = 1;
      this.setEmoteAnimation(undefined);
    }
  }
  onVRControllerRightSqueezeEnd_() {
    this.moveDirection.jump = 0;
  }

  playKeyDownHandler_(event) {
    if (this.chatMode || this.landMode) return;
    this.pressedKeys[event.code] = true;

    // check one more time orbit.keyActive
    if (
      this.orbit.keyActive.LEFT ||
      this.orbit.keyActive.UP ||
      this.orbit.keyActive.RIGHT ||
      this.orbit.keyActive.BOTTOM ||
      this.orbit.keyActive.FORWARD ||
      this.orbit.keyActive.SHIFT ||
      this.orbit.keyActive.BACKWARD
    ) {
      this.orbit.keyActive = {LEFT: false, UP: false, RIGHT: false, BOTTOM: false, FORWARD: false, SHIFT: false, BACKWARD: false};
    }

    if (this.avatar?.model !== undefined && this.isAvatarControllerActive) {
      this.handleAvatarController(event);
    }

    switch (event.code) {
      case "KeyI":
        if (event.ctrlKey) {
          if (this.showStats === false) {
            this.renderer.glRenderer.info.autoReset = false;
            this.debuggerUI.show();
            this.statsDom.style.display = "";
            this.showStats = true;
          } else {
            this.renderer.glRenderer.info.autoReset = true;
            this.debuggerUI.hide();
            this.statsDom.style.display = "none";
            this.showStats = false;
          }
        }
        break;
      case "KeyT":
        if (process.env.NODE_ENV === "development") {
          if (this.physics) {
            if (this.physics.debugDrawer.enabled) {
              this.physics.debug.disable();
            } else {
              this.physics.debug.enable();
            }
          }
        }
        break;
      case "KeyY":
        if (process.env.NODE_ENV === "development") {
          if (event.ctrlKey) {
            const searched = [];
            const keyword = window.prompt("keyword?");
            this.scenePlay.traverse(child => {
              if (child.name && child.name.includes(keyword)) {
                searched.push(child);
              }
            });
            console.log("SEARCH RESULT", searched);
          } else {
            console.log("SCENE PLAY", this.scenePlay);
          }
        }
        break;
      case "ShiftLeft":
        if (this.avatar?.model !== undefined) this.isAvatarBoosted = true;
        break;
      // case "KeyP":
      //   this.GLTFLoader.load("asset/rpm/asset/machine_rifle_001.glb", (obj) => {
      //     const rifle = obj.scene;
      //     const rightHand = this.scenePlay.getObjectByName("RightHand");
      //     rightHand.add(rifle);
      //     rifle.position.set(0.14, 0.23, 0);
      //     const euler = new THREE.Euler(1.55, 3.1, 1.7);
      //     rifle.setRotationFromEuler(euler);
      //     rifle.scale.set(0.15, 0.15, 0.15);
      //   },
      //   undefined,
      //   (err) => {
      //     console.error(err);
      //   });
      //   break;
      default:
        if (this.keyDownCallbacks[event.code]) {
          this.keyDownCallbacks[event.code](this.scriptLauncher.getPlayer());
        }
        if (this.gameServer.room && event.repeat === false) {
          this.gameServer.sendInputToServer("key", {
            keyCode: event.code,
            isPressed: true,
          });
        }
        break;
    }
  }

  playKeyUpHandler_(event) {
    this.pressedKeys[event.code] = false;

    if (this.avatar?.model !== undefined && this.isAvatarControllerActive) {
      this.handleAvatarController(event);
    }

    switch (event.code) {
      default:
        if (this.keyUpCallbacks[event.code]) {
          this.keyUpCallbacks[event.code]();
        }
        if (this.gameServer.room) {
          this.gameServer.sendInputToServer("key", {
            keyCode: event.code,
            isPressed: false,
          });
        }
        break;
    }
  }

  handleAvatarController = (() => {
    const avatarController = {
      default: {
        keydown: {
          KeyW: e => {
            this.moveDirection.forward = 1;
            this.setEmoteAnimation(undefined);
          },
          KeyA: e => {
            this.moveDirection.left = 1;
            this.setEmoteAnimation(undefined);
          },
          KeyS: e => {
            this.moveDirection.back = 1;
            this.setEmoteAnimation(undefined);
          },
          KeyD: e => {
            this.moveDirection.right = 1;
            this.setEmoteAnimation(undefined);
          },
          Space: e => {
            this.moveDirection.jump = 1;
            this.setEmoteAnimation(undefined);
          },
        },
        keyup: {
          KeyW: e => {
            this.moveDirection.forward = 0;
          },
          KeyA: e => {
            this.moveDirection.left = 0;
          },
          KeyS: e => {
            this.moveDirection.back = 0;
          },
          KeyD: e => {
            this.moveDirection.right = 0;
          },
          Space: e => {
            this.moveDirection.jump = 0;
          },
        },
      },
    };

    return event => {
      const controller = avatarController[this.avatar.controller];
      if (!controller?.[event.type]?.[event.code]) return;
      controller[event.type][event.code](event);
    };
  })();

  // [TMP] for test (temperature, battery)
  toggleCollisionDetection() {
    this.isCollisionEnabled = this.isCollisionEnabled === undefined ? false : !this.isCollisionEnabled;
  }

  resizeHandler_(width = this.canvas.clientWidth, height = this.canvas.clientHeight) {
    if (width instanceof Event) {
      // In this case, the resizeHandler is invoked by the resize event,
      // therefore, this function has to use the this.canvas.clientWidth as a default value.
      width = this.canvas.clientWidth;
      height = this.canvas.clientHeight;
      // Temporal way to resize canvas - remove auto created inline style
      this.canvas.removeAttribute("style");
      // note:
      // Even if the "width" comes with a value "0", the code will go on.
    }
    if (this.scenePlayCameras) {
      this.scenePlayCameras.forEach(cam => {
        if (cam.isPerspectiveCamera) {
          cam.renderer.composer.setSize(width, height, false);
          cam.aspect = width / height;
          cam.updateProjectionMatrix();
        }
      });
    }

    // TODO : Resize
    this.renderer.glRenderer.setSize(width, height, false);
    this.renderer.composer.setSize(width, height, false);
    // this.renderer.resize(width, this.canvas.clientHeight, false);
    this.labelRenderer.setSize(width, height, false);
    this.editCamera.aspect = width / height;
    this.editCamera.updateProjectionMatrix();

    this.guiCamera.left = -width / 2;
    this.guiCamera.right = width / 2;
    this.guiCamera.top = height / 2;
    this.guiCamera.bottom = -height / 2;
    this.guiCamera.updateProjectionMatrix();

    this.sceneGUI.traverse(sprite => {
      this.updateSpriteTransform(sprite);
    });

    // camera preview
    this.previewSprite.scale.set(width / 4, height / 4);
    this.previewSprite.position.set(width * (-3 / 8), height * (-3 / 8), 0);

    this.render();
  }

  visibilityHandler_() {
    if (document.visibilityState === "visible") {
    } else if (document.visibilityState === "hidden") {
      this.moveDirection.jump = 0;
      this.moveDirection.right = 0;
      this.moveDirection.left = 0;
      this.moveDirection.forward = 0;
      this.moveDirection.back = 0;
    }
  }

  animate_() {
    this.render();
    // requestAnimationFrame(this.animate);
  }

  addMesh(scene, name, parent, index) {
    let mesh = Mesh.createMesh(scene, name, parent, index);
    this.postAddObject(mesh);
    this.selectObject(mesh);
    this.dispatchEvent(new CustomEvent(ACTION_TYPE.OBJECT_ADDED, {detail: {object: mesh}}));
    this.assetObjects[mesh.uuid] = mesh;
    return mesh;
  }

  addLight(type) {
    let light = Light.createLight(type);
    this.postAddObject(light);
    this.selectObject(light);
    this.dispatchEvent(new CustomEvent(ACTION_TYPE.OBJECT_ADDED, {detail: {object: light}}));
    return light;
  }

  addCamera(type) {
    let camera = Camera.createCamera(this, type);
    this.postAddObject(camera);
    this.selectObject(camera);
    this.dispatchEvent(new CustomEvent(ACTION_TYPE.OBJECT_ADDED, {detail: {object: camera}}));
    return camera;
  }

  addStartLocation(url) {
    // [TEMP] The StartLocation.createStartLocation function is no longer necessary.
    // So it should be replaced with StartLocation.createSpawnPoint
    // after the Indie Challengers event ends.
    // In summary, you only need to leave the 'else' scope.
    if (url === "https://d2f4dt8igm02oj.cloudfront.net/redbrick-asset/spawn/starting_point.glb") {
      StartLocation.createStartLocation(url, startLocation => {
        this.postAddObject(startLocation);
        this.selectObject(startLocation);
        this.dispatchEvent(
          new CustomEvent(ACTION_TYPE.OBJECT_ADDED, {
            detail: {object: startLocation},
          }),
        );
        this.startLocation = startLocation;
        return startLocation;
      });
    } else {
      StartLocation.createSpawnPoint(this, url, spawnPoint => {
        this.postAddObject(spawnPoint);
        this.selectObject(spawnPoint);
        this.dispatchEvent(
          new CustomEvent(ACTION_TYPE.OBJECT_ADDED, {
            detail: {object: spawnPoint},
          }),
        );
        this.startLocation = spawnPoint;
        return spawnPoint;
      });
    }
  }

  addVideo(name, source) {
    let video = Video.createVideo(name, source);
    this.postAddObject(video);
    this.selectObject(video);
    this.dispatchEvent(new CustomEvent(ACTION_TYPE.OBJECT_ADDED, {detail: {object: video}}));
    this.assetObjects[video.uuid] = video;
    return video;
  }

  addImage(name, source) {
    let image = Image.createImage(name, source);
    this.postAddObject(image);
    this.selectObject(image);
    this.dispatchEvent(new CustomEvent(ACTION_TYPE.OBJECT_ADDED, {detail: {object: image}}));
    this.assetObjects[image.uuid] = image;
    return image;
  }

  addPortal() {
    Portal.createPortal(model => {
      this.postAddObject(model);
      this.selectObject(model);
      this.dispatchEvent(new CustomEvent(ACTION_TYPE.OBJECT_ADDED, {detail: {object: model}}));
      this.assetObjects[model.uuid] = model;
      return model;
    });
  }

  addGui(name, thumbnail) {
    this.TextureLoader.load(thumbnail, texture => {
      // TODO : transparency check
      texture.encoding = THREE.sRGBEncoding;
      const material = new THREE.SpriteMaterial({map: texture});
      const sprite = new THREE.Sprite(material);
      sprite.name = name;
      sprite.userData.guiTransform = {
        center: {x: 0.5, y: 0.5},
        anchor: {x: "Center", y: "Center"},
        offset: {x: {value: 0, unit: "px"}, y: {value: 0, unit: "px"}},
        size: {
          x: {value: texture.image.width, unit: "px"},
          y: {value: texture.image.height, unit: "px"},
        },
        constantAspectRatio: true,
      };

      const cssDiv = document.createElement("div");
      cssDiv.className = "label";
      cssDiv.textContent = "";
      cssDiv.style.fontSize = "20px";
      cssDiv.style.color = "#ffffff";
      cssDiv.style.pointerEvents = "none";
      // TEMP - set css style top to 0
      cssDiv.style.top = "0px";
      cssDiv.style.whiteSpace = "pre-wrap";
      this.cssLabel = new CSS2DObject(cssDiv);
      this.cssLabel.position.set(0, 0, 0);
      sprite.add(this.cssLabel);
      sprite.userData.css = {
        className: cssDiv.className,
        textContent: cssDiv.textContent,
        fontSize: cssDiv.style.fontSize,
        fontWeight: cssDiv.style.fontWeight,
        fontColor: cssDiv.style.color,
        top: cssDiv.style.top,
        fontFamily: "",
      };

      this.postAddObject(sprite);

      sprite.scale.set(texture.image.width, texture.image.height, 1);
      this.selectObject(sprite);

      this.dispatchEvent(new CustomEvent(ACTION_TYPE.OBJECT_ADDED, {detail: {object: sprite}}));
    });
  }

  addAdBannerGui(name, thumbnail, adInfo) {
    this.TextureLoader.load(thumbnail, texture => {
      texture.encoding = THREE.sRGBEncoding;
      const material = new THREE.SpriteMaterial({map: texture});
      const sprite = new THREE.Sprite(material);
      sprite.name = name;
      sprite.userData.guiTransform = {
        center: {x: 0.5, y: 0.5},
        anchor: {x: "Center", y: "Center"},
        offset: {x: {value: 0, unit: "px"}, y: {value: 0, unit: "px"}},
        size: {
          x: {value: texture.image.width, unit: "px"},
          y: {value: texture.image.height, unit: "px"},
        },
        constantAspectRatio: true,
      };

      const cssDiv = document.createElement("div");
      cssDiv.className = "label";
      cssDiv.textContent = "";
      cssDiv.style.fontSize = "20px";
      cssDiv.style.color = "#ffffff";
      cssDiv.style.pointerEvents = "none";
      // TEMP - set css style top to 0
      cssDiv.style.top = "0px";
      cssDiv.style.whiteSpace = "pre-wrap";
      this.cssLabel = new CSS2DObject(cssDiv);
      this.cssLabel.position.set(0, 0, 0);
      sprite.add(this.cssLabel);
      sprite.userData.css = {
        className: cssDiv.className,
        textContent: cssDiv.textContent,
        fontSize: cssDiv.style.fontSize,
        fontWeight: cssDiv.style.fontWeight,
        fontColor: cssDiv.style.color,
        top: cssDiv.style.top,
        fontFamily: "",
      };

      sprite.userData.adInfo = {...JSON.parse(adInfo)};
      this.postAddObject(sprite);

      sprite.scale.set(texture.image.width, texture.image.height, 1);
      this.selectObject(sprite);

      this.dispatchEvent(new CustomEvent(ACTION_TYPE.OBJECT_ADDED, {detail: {object: sprite}}));
    });
  }

  addParticleSystem(textureImg) {
    const particleSystem = new CustomParticleSystem(textureImg);
    particleSystem.createSystem();
    this.editParticleSystemArray.push(particleSystem.system);
    this.postAddObject(particleSystem);
    this.selectObject(particleSystem);
    this.dispatchEvent(
      new CustomEvent(ACTION_TYPE.OBJECT_ADDED, {
        detail: {object: particleSystem},
      }),
    );
    return particleSystem;
  }

  addAudio(name, info) {
    let audio = Audio.createAudio(name, info);
    this.postAddObject(audio);
    this.selectObject(audio);
    this.dispatchEvent(new CustomEvent(ACTION_TYPE.OBJECT_ADDED, {detail: {object: audio}}));
    return audio;
  }

  addDemoModel(name, path) {
    Demo.createDemoModel(name, path, model => {
      this.postAddObject(model);
      this.selectObject(model);
      this.dispatchEvent(new CustomEvent(ACTION_TYPE.OBJECT_ADDED, {detail: {object: model}}));
      this.assetObjects[model.uuid] = model;
      return model;
    });
  }

  postAddObject(object, parent, index, fromUndoRedo = false) {
    object.key = object.uuid;
    object.title = object.name + "(" + object.uuid.substring(0, 3) + ")";
    object.visible = true;
    object.userData.isSprite = true;

    if (object.isSprite) {
      if (fromUndoRedo) {
        object.traverse(obj => {
          if (obj.isCSS2DObject) obj.element.style.visibility = "visible";
        });
      }

      this.objects2D.push(object);
      if (parent === undefined) {
        this.sceneGUI.add(object);
      } else {
        parent.children.splice(index, 0, object);
        object.parent = parent;
      }

      this.reorderZIndex(object.parent);

      // Dragcontrol for sprite translation
      this.dragControl.dispose();
      this.dragControl = new DragControls([...this.sceneGUI.children], this.guiCamera, this.labelRenderer.domElement);
      this.dragControl.addEventListener("dragstart", this.dragstartSprite);
      this.dragControl.addEventListener(
        "drag",
        _.throttle(() => this.render(), 35),
      );
      this.dragControl.addEventListener("dragend", this.dragendSprite);
    } else {
      // TODO:
      // logic will be changed when fix draggable tree
      // need to define object hierarchy structure first

      if (this.world && !fromUndoRedo) this.setSpawnPoint(object);

      this.objects.push(object);
      this.addHelper(object);

      if (parent === undefined) {
        this.sceneEdit.add(object);
      } else {
        parent.children.splice(index, 0, object);
        object.parent = parent;
      }
    }

    this.render();

    if (this.world && !fromUndoRedo) {
      let commandObj = {
        type: Command.typeAdd,
        target: object,
        parent: object.parent,
      };
      let command = new Command(commandObj);
      this.undo.push(command);
      this.redo.length = 0;
    }
  }

  setSpawnPoint(object) {
    this.editCamera.getWorldDirection(this._cameraDirection);
    this._cameraRaycaster.setFromCamera({x: 0, y: 0}, this.editCamera);
    const collided = this._cameraRaycaster.intersectObjects(
      this.sceneEdit.children.filter(obj => obj.userData.type !== "particleSystem"),
      true,
    );
    if (collided.length) {
      const target = collided[0];
      const point = target.point;
      const normal = target.face.normal.transformDirection(target.object.matrixWorld);
      this._boundingBox.setFromObject(object);
      let offset;
      if (this._boundingBox.isEmpty()) {
        offset = 5;
      } else if (normal.angleTo(this.yAxis) < 0.1) {
        offset = (this._boundingBox.max.y - this._boundingBox.min.y) / 2;
      } else {
        offset = this._boundingBox.max.distanceTo(this._boundingBox.min) / 2;
      }
      this._spawnPoint.copy(point).addScaledVector(normal, offset);
    } else {
      this._spawnPoint.set(
        this.editCamera.position.x + this._cameraDirection.x * 25,
        this.editCamera.position.y + this._cameraDirection.y * 25,
        this.editCamera.position.z + this._cameraDirection.z * 25,
      );
    }
    this._spawnPoint.x = Number(this._spawnPoint.x.toFixed(2));
    this._spawnPoint.y = Number(this._spawnPoint.y.toFixed(2));
    this._spawnPoint.z = Number(this._spawnPoint.z.toFixed(2));
    object.position.copy(this._spawnPoint);
  }

  removeObject(object, fromUndoRedo = false) {
    let scope = this;
    if (object === undefined || object.parent === undefined || object.parent === null) return;

    if (!fromUndoRedo) {
      let commandObj = {
        type: Command.typeRemove,
        target: object,
        parent: object.parent,
      };
      let command = new Command(commandObj);
      this.undo.push(command);
      this.redo.length = 0;
    }

    object.traverse(function (child) {
      // this.removeCamera(child);
      scope.removeHelper(child);
      // if (child.material !== undefined) this.removeMaterial(child.material);
    });
    const parent = object.parent;
    object.parent.remove(object);
    if (object === this.transform.object) {
      scope.transform.detach();
    }

    scope.objects.splice(scope.objects.indexOf(object), 1);
    if (object.isSprite) {
      object.traverse(obj => {
        if (obj.isCSS2DObject) obj.element.style.visibility = "hidden";
      });
      scope.objects2D.splice(scope.objects2D.indexOf(object), 1);

      scope.dragControl.dispose();
      scope.dragControl = new DragControls([...scope.sceneGUI.children], scope.guiCamera, scope.labelRenderer.domElement);

      scope.dragControl.addEventListener("dragstart", scope.dragstartSprite);
      scope.dragControl.addEventListener("drag", event => {
        scope.render();
      });
      scope.dragControl.addEventListener("dragend", scope.dragendSprite);

      this.reorderZIndex(parent);
    }

    if (scope.selected === object) {
      scope.selectObject(undefined);
    }

    // send event to react container
    this.dispatchEvent(new CustomEvent(ACTION_TYPE.DELETE_OBJECT, {detail: {object: undefined}}));
    scope.render();
  }

  copyObject(object) {
    let newObj = object.clone();
    let meshObject;
    if (newObj.isMesh) {
      newObj.material = newObj.material.clone();
    } else if (!newObj.isMesh) {
      meshObject = newObj.getObjectByProperty("isMesh", true);
      if (meshObject) meshObject.material = meshObject.material.clone();
    }

    newObj.key = newObj.uuid;
    newObj.title = newObj.name + "(" + newObj.uuid.substring(0, 3) + ")";
    newObj.visible = true;
    delete newObj.userData.title;

    this.postAddObject(newObj);
    this.selectObject(newObj);
    if (newObj.isSprite) {
      // sprite's z-position is controlled by 'reorderZIndex' method.
      newObj.position.set(object.position.x, object.position.y);
    } else {
      newObj.position.set(object.position.x, object.position.y, object.position.z);
    }
    this.dispatchEvent(new CustomEvent(ACTION_TYPE.OBJECT_ADDED, {detail: {object: newObj}}));
    if (this.box.isEmpty() === false) {
      this.selectionBox.setFromObject(this.selected);
      this.selectionBox.visible = true;
    }
    this.selected = newObj;
    this.render();
  }

  addHelper(object, helper) {
    var geometry = new THREE.SphereGeometry(2, 4, 2);
    var material = new THREE.MeshBasicMaterial({
      color: 0xff0000,
      visible: false,
    });

    let textureUrl;

    if (helper === undefined) {
      if (object.isPerspectiveCamera) {
        helper = new THREE.CameraHelper(object);
        textureUrl = "asset/helpers/perspective.png";
      } else if (object.isOrthographicCamera) {
        helper = new THREE.CameraHelper(object);
        textureUrl = "asset/helpers/orthographic.png";
      } else if (object.isPointLight) {
        helper = new THREE.PointLightHelper(object, 1);
        textureUrl = "asset/helpers/point.png";
      } else if (object.isDirectionalLight) {
        helper = new THREE.DirectionalLightHelper(object, 1);
        textureUrl = "asset/helpers/directional.png";
      } else if (object.isSpotLight) {
        helper = new THREE.SpotLightHelper(object);
        textureUrl = "asset/helpers/spot.png";
      } else if (object.isHemisphereLight) {
        helper = new THREE.HemisphereLightHelper(object, 1);
        textureUrl = "asset/helpers/hemisphere.png";
      } else if (object.isSkinnedMesh) {
        helper = new THREE.SkeletonHelper(object.skeleton.bones[0]);
      } else if (object.isAmbientLight) {
        helper = new THREE.SkeletonHelper(object);
        textureUrl = "asset/helpers/ambient.png";
      } else if (object.userData.type === "audio") {
        helper = new THREE.SkeletonHelper(object);
        textureUrl = "asset/helpers/sound.png";
      } else {
        // no helper for this object type
        return;
      }

      this.TextureLoader.load(textureUrl, texture => {
        const helperSpriteMaterial = new THREE.SpriteMaterial({map: texture});
        const helperSprite = new THREE.Sprite(helperSpriteMaterial);
        helper.add(helperSprite);
        this.render();
      });

      var picker = new THREE.Mesh(geometry, material);
      picker.name = "picker";
      picker.userData.object = object;
      helper.add(picker);
      this.objects.push(picker);
    }

    this.sceneHelpers.add(helper);
    this.helpers[object.id] = helper;
  }

  removeHelper(object) {
    if (this.helpers[object.id] === undefined) {
      return;
    }

    let helper = this.helpers[object.id];
    helper.parent.remove(helper);
    delete this.helpers[object.id];

    let picker = helper.getObjectByName("picker");
    if (picker !== undefined) {
      this.objects.splice(this.objects.indexOf(picker), 1);
    }
  }

  toggleHelper(flag) {
    this.showSceneHelpers = flag;
    this.render();
  }

  toggleHelperVisibility(helperType, isVisible) {
    for (let x in this.helpers) {
      if (this.helpers[x].light) {
        if (this.helpers[x].light.uuid === helperType) {
          this.helpers[x].visible = isVisible;
        }
      } else if (this.helpers[x].root) {
        if (this.helpers[x].root.uuid === helperType) {
          this.helpers[x].visible = isVisible;
        }
      } else if (this.helpers[x].camera) {
        if (this.helpers[x].camera.uuid === helperType) {
          this.helpers[x].visible = isVisible;
        }
      }
    }
  }
  toggleGrid(flag) {
    this.showGrid = flag;
    this.render();
  }

  toggleVisibility(object, visible) {
    if (!object) return;
    if (visible) {
      object.traverse(child => {
        if (child.layers && child.userData.originalLayerMask !== undefined) {
          child.layers.mask = child.userData.originalLayerMask;
          delete child.userData.originalLayerMask;
        }
      });
    } else {
      object.traverse(child => {
        if (child.layers && child.userData.originalLayerMask === undefined) {
          child.userData.originalLayerMask = child.layers.mask;
          child.layers.set(4);
        }
      });
    }
    this.render();
    this.dispatchEvent(
      new CustomEvent(ACTION_TYPE.CHANGE_PROPERTY_FROM_SCENE, {
        detail: {object},
      }),
    );
  }

  updateProperty(category, action) {
    _updateProperty(this, category, action);
  }

  updateSpriteTransform = (function () {
    const anchorFactorX = {"Far-Left": -0.5, Center: 0, "Far-Right": 0.5};
    const anchorFactorY = {Bottom: -0.5, Center: 0, Top: 0.5};

    const _vector3 = new THREE.Vector3();

    return function (sprite) {
      if (sprite.type !== "Sprite") return;

      const {width: imageWidth, height: imageHeight} = sprite.material.map.image;
      const aspectRatio = imageWidth / imageHeight;
      let {anchor, offset, size, constantAspectRatio} = sprite.userData.guiTransform;
      if (this.versionManager.checkCompatibility.script.isOOP() && this.mode === MODE_TYPE.PLAY_MODE) {
        anchor = sprite.anchor || anchor;
        offset = sprite.offset || offset;
        size = sprite.size || size;
        constantAspectRatio = sprite.constantAspectRatio || constantAspectRatio;
      }

      let parentWidth = this.canvas.clientWidth;
      let parentHeight = this.canvas.clientHeight;
      let parentCenterOffsetX = 0;
      let parentCenterOffsetY = 0;
      let scaleFactorX = 1;
      let scaleFactorY = 1;
      if (sprite.parent.type === "Sprite") {
        sprite.parent.getWorldScale(_vector3);
        parentWidth = scaleFactorX = _vector3.x;
        parentHeight = scaleFactorY = _vector3.y;
        parentCenterOffsetX = _vector3.x * (0.5 - sprite.parent.center.x);
        parentCenterOffsetY = _vector3.y * (0.5 - sprite.parent.center.y);
      }

      const offsetX = offset.x.value * (offset.x.unit === "px" ? 1 : parentWidth / 100);
      const offsetY = offset.y.value * (offset.y.unit === "px" ? 1 : parentHeight / 100);
      const sizeX = size.x.value * (size.x.unit === "px" ? 1 : parentWidth / 100);
      const sizeY = size.y.value * (size.y.unit === "px" ? 1 : parentHeight / 100);
      const positionX = parentCenterOffsetX + anchorFactorX[anchor.x] * parentWidth + offsetX;
      const positionY = parentCenterOffsetY + anchorFactorY[anchor.y] * parentHeight + offsetY;
      const scaleX = constantAspectRatio ? Math.min(sizeX, sizeY * aspectRatio) : sizeX;
      const scaleY = constantAspectRatio ? Math.min(sizeY, sizeX / aspectRatio) : sizeY;

      sprite.position.set(positionX / scaleFactorX, positionY / scaleFactorY);
      sprite.scale.set(scaleX / scaleFactorX, scaleY / scaleFactorY);

      const textElement = sprite.children.find(child => child.isCSS2DObject).element;
      if (textElement) {
        const fontSize = sprite.userData.css.fontSize;
        const fontSizeNum = Number(fontSize.substring(0, fontSize.length - 2));
        const scaleFactor = Math.min(scaleX / imageWidth, scaleY / imageHeight);

        textElement.style.marginLeft = `${scaleX * (0.5 - sprite.center.x)}px`;
        textElement.style.marginTop = `${scaleY * (sprite.center.y - 0.5)}px`;
        textElement.style.fontSize = `${fontSizeNum * scaleFactor}px`;
      }
    };
  })();

  updateSpriteTransformFromScene = (function () {
    const anchorFactorX = {"Far-Left": -0.5, Center: 0, "Far-Right": 0.5};
    const anchorFactorY = {Bottom: -0.5, Center: 0, Top: 0.5};

    const _vector3 = new THREE.Vector3();

    return function (sprite) {
      const {anchor, offset} = sprite.userData.guiTransform;

      let parentWidth = this.canvas.clientWidth;
      let parentHeight = this.canvas.clientHeight;
      let parentCenterOffsetX = 0;
      let parentCenterOffsetY = 0;
      let scaleFactorX = 1;
      let scaleFactorY = 1;
      if (sprite.parent.type === "Sprite") {
        sprite.parent.getWorldScale(_vector3);
        parentWidth = scaleFactorX = _vector3.x;
        parentHeight = scaleFactorY = _vector3.y;
        parentCenterOffsetX = _vector3.x * (0.5 - sprite.parent.center.x);
        parentCenterOffsetY = _vector3.y * (0.5 - sprite.parent.center.y);
      }

      const offsetX =
        (sprite.position.x * scaleFactorX - parentCenterOffsetX - anchorFactorX[anchor.x] * parentWidth) *
        (offset.x.unit === "px" ? 1 : 100 / parentWidth);
      const offsetY =
        (sprite.position.y * scaleFactorY - parentCenterOffsetY - anchorFactorY[anchor.y] * parentHeight) *
        (offset.y.unit === "px" ? 1 : 100 / parentHeight);

      sprite.userData.guiTransform.offset.x.value = Number(offsetX.toFixed(2));
      sprite.userData.guiTransform.offset.y.value = Number(offsetY.toFixed(2));
    };
  })();

  updateAudioHelper(object) {
    if (object.userData.type !== "audio" && !object.userData.hasAudio) {
      this.audioHelper.traverse(child => (child.visible = false));
      return;
    }

    const {
      position,
      rotation,
      userData: {audioInfo},
    } = object;
    if (!audioInfo.positional.isActive) {
      this.audioHelper.traverse(child => (child.visible = false));
      return;
    } else {
      this.audioHelper.traverse(child => (child.visible = true));
    }

    this.audioHelper.position.set(position.x, position.y, position.z);
    this.audioHelper.setRotationFromEuler(rotation);

    const {distanceModel, refDistance, maxDistance, directionalCone} = audioInfo.positional;
    const coneInnerAngle = directionalCone.isActive ? directionalCone.coneInnerAngle : 360;
    const coneOuterAngle = directionalCone.isActive ? directionalCone.coneOuterAngle : 0;
    this.audioHelper.setDirectionalCone(coneInnerAngle, coneOuterAngle, directionalCone.coneOuterGain);

    this.audioRefDistanceRange.range = refDistance;
    this.audioRefDistanceRange.update();
    if (distanceModel === "linear") {
      this.audioMaxDistanceRange.range = maxDistance;
      this.audioMaxDistanceRange.update();
    } else {
      this.audioMaxDistanceRange.visible = false;
    }
  }

  parseJSON(json) {
    try {
      if (json[0] === "[") {
        return decompress(JSON.parse(json));
      } else {
        return JSON.parse(json);
      }
    } catch (error) {
      console.log(error);
    }
  }

  compressScene(scene) {
    try {
      let compressedScene;

      scene = JSON.stringify(scene);
      scene = JSON.parse(scene);
      // TODO : FIND NEW COMPRESS LOGIC - FLOATING POINT ISSUE
      // compressedScene = compress(scene);
      compressedScene = scene;

      return compressedScene;
    } catch (error) {
      console.log(error);
    }
  }

  saveFile(name) {
    _saveFile(this, name);
  }

  uploadFile(publishInfo) {
    _publishWorld(this, publishInfo);
  }

  saveProject() {
    _saveProject(this);
  }

  autoSaveProject() {
    _autoSaveProject(this);
  }

  createIcon() {
    var strMime = "image/jpeg";
    let imgData = this.canvas.toDataURL(strMime);
    let icon = Buffer.from(imgData.replace(/^data:image\/\w+;base64,/, ""), "base64");
    return icon;
  }

  async loadFile(name) {
    _loadFile(this, name);
  }

  loadGlb() {
    let form = document.createElement("form");
    form.style.display = "none";
    document.body.appendChild(form);

    let fileInput = document.createElement("input");
    fileInput.multiple = true;
    fileInput.type = "file";
    fileInput.addEventListener("change", () => {
      let extension;
      for (let i = 0; i < fileInput.files.length; i++) {
        extension = fileInput.files[i].name.split(".").pop().toLowerCase();
        if (extension !== "glb" && extension !== "gltf") {
          console.error("You can import only glb/gltf format.");
          return;
        }
      }

      Object.keys(fileInput.files).forEach(i => {
        let reader = new FileReader();

        const fileName = fileInput.files[i].name.slice(0, -4);
        reader.addEventListener("load", event => {
          const contents = event.target.result;
          const loader = new GLTFLoader();
          loader.setMeshoptDecoder(MeshoptDecoder);
          loader.parse(
            contents,
            "",
            object => {
              Glb.addGlbModel(object.scene, fileName, model => {
                model.animations = object.animations;
                this.postAddObject(model);
                this.selectObject(model);
                this.dispatchEvent(
                  new CustomEvent(ACTION_TYPE.OBJECT_ADDED, {
                    detail: {object: model},
                  }),
                );
                this.assetObjects[model.uuid] = model;
                return model;
              });
            },
            e => {
              console.error(e);
            },
          );
        });
        reader.readAsArrayBuffer(fileInput.files[i]);
      });
      form.reset();
    });
    form.appendChild(fileInput);
    fileInput.click();
    this.render();
  }

  loadFromPID(targetPID) {
    let url = URL.S3_PROJECT_STORAGE + "/" + targetPID + ".json";
    let altUrl = URL.S3_WORLD_STORAGE + "/" + targetPID + ".json";
    _editModeFetchFromS3CopyPID(this, url, altUrl);
  }
  exportGlb() {
    const exporter = new GLTFExporter();

    exporter.parse(
      this.sceneEdit,
      result => {
        const blob = new Blob([result], {type: "application/octet-stream"});
        const link = document.createElement("a");

        if (link.href) {
          window.URL.revokeObjectURL(link.href);
        }
        link.href = window.URL.createObjectURL(blob);
        link.download = "scene.glb";
        link.dispatchEvent(new MouseEvent("click"));
      },
      undefined,
      {binary: true},
    );
  }

  postLoadSceneFile(scene) {
    scene.children.forEach(object => {
      if (!object.userData.type) {
        // [TODO] general한 userData setting이 정해진 이후 작업 필요
        object.userData.isSprite = true;
        object.userData.canCollide = true;
        if (object.type === "Mesh") {
          object.userData.type = "mesh";
        } else if (object.type === "Group") {
          object.userData.type = "asset";
          object.userData.isKinematic = true;
        }
        object.traverse(child => {
          if (child.type === "Mesh" || child.type === "Group") {
            child.layers.set(1);
          }
        });
      }
    });
    if (!scene.userData.postProcessing) {
      scene.userData.postProcessing = {
        ssao: {},
        bloom: {},
        godrays: {},
        hueSaturation: {},
        brightnessContrast: {},
      };
    }
    return scene;
  }

  resetEditScene(scene) {
    this.transform.detach();
    this.helpers.forEach(helper => {
      const object = helper.getObjectByName("picker")?.userData.object;
      if (!object) return;
      this.removeHelper(object);
    });
    this.sceneEdit = {};
    this.selected = undefined;
    this.selectionBox.visible = false;

    this.sceneEdit = scene;
    this.sceneEdit.key = this.sceneEdit.uuid;
    this.sceneEdit.title = this.sceneEdit.name;
    this.objects = [];
    this.sceneEdit.traverse(child => {
      if (child.userData.type === "asset" || child.userData.type === "mesh") {
        if (!child.userData.isMassless) this.assetObjects[child.uuid] = child;
      }
      if (child.userData.isSprite || child.userData.type === "ground") {
        child.key = child.uuid;
        child.title = child.userData.title || child.name + "(" + child.uuid.substring(0, 3) + ")";
      }
      if (child.userData.type !== "sky" && child.userData.type !== "ground" && child.type !== "Scene") {
        this.objects.push(child);
        this.addHelper(child);
      }
      if (child.material && child.material.opacity === 1) {
        // TEMP - need to check image source alpha
        if (child.userData.type !== "image") {
          child.material.transparent = false;
        } else {
          child.material.transparent = true;
        }
      }
    });
    // If there is no postprocessing info, create it.
    if (!this.sceneEdit.userData.postProcessing) {
      this.sceneEdit.userData.postProcessing = {
        ssao: {},
        bloom: {},
        godrays: {},
        hueSaturation: {},
        brightnessContrast: {},
      };
    }
    this.dispatchEvent(
      new CustomEvent(ACTION_TYPE.ADD_SCENE, {
        detail: {object: this.sceneEdit},
      }),
    );
    if (this.tutorial) this.tutorial.init();
    this.setupEditModeParticleSystem();
    this.renderer.updatePostProcess(this.sceneEdit, this.editCamera);
    if (this.sceneEdit.userData.toneMapping.mode !== undefined) {
      this.renderer.glRenderer.toneMapping = this.sceneEdit.userData.toneMapping.mode;
      this.renderer.glRenderer.toneMappingExposure = this.sceneEdit.userData.toneMapping.exposure;
    }
    this.render();
    this.updateCodeEditorMode();
  }

  resetGUIScene(scene) {
    const css2DObjects = [];
    this.sceneGUI.traverse(object => {
      if (object.isCSS2DObject) css2DObjects.push(object);
    });
    for (let i = css2DObjects.length - 1; i >= 0; i--) css2DObjects[i].removeFromParent();

    this.sceneGUI = scene;
    this.sceneGUI.key = this.sceneGUI.uuid;
    this.sceneGUI.title = this.sceneGUI.name;
    this.objects2D = [];
    this.sceneGUI.traverse(child => {
      if (child.userData.isSprite) {
        child.key = child.uuid;
        child.title = child.userData.title || child.name + "(" + child.uuid.substring(0, 3) + ")";
        child.rotation.set(0, 0, 0);
        this.objects2D.push(child);

        // recreate css2DObject by userData
        const idx = child.children.findIndex(obj => obj.type === "Object3D");
        if (idx !== -1) {
          child.children[idx].removeFromParent();
          const cssDiv = document.createElement("div");
          cssDiv.className = child.userData.css.className;
          cssDiv.textContent = child.userData.css.textContent;
          cssDiv.style.fontSize = child.userData.css.fontSize;
          cssDiv.style.color = child.userData.css.fontColor;
          cssDiv.style.fontFamily = child.userData.css.fontFamily;
          // TEMP - set css style top to 0
          cssDiv.style.top = child.userData.css.top ? child.userData.css.top : "0px";
          cssDiv.style.pointerEvents = "none";
          cssDiv.style.whiteSpace = "pre-wrap";
          const cssLabel = new CSS2DObject(cssDiv);
          // add objects2D for selectObject
          child.add(cssLabel);
          child.children.pop();
          child.children.splice(idx, 0, cssLabel);
        }

        if (!child.userData.guiTransform) {
          const {clientWidth, clientHeight} = this.canvas;
          const offsetX = Number(child.position.x.toFixed(2));
          const offsetY = Number(child.position.y.toFixed(2));
          const sizeX = Number(((child.scale.x / clientWidth) * 100).toFixed(2));
          const sizeY = Number(((child.scale.y / clientHeight) * 100).toFixed(2));
          child.userData.guiTransform = {
            anchor: {x: "Center", y: "Center"},
            offset: {
              x: {value: offsetX, unit: "px"},
              y: {value: offsetY, unit: "px"},
            },
            size: {x: {value: sizeX, unit: "%"}, y: {value: sizeY, unit: "%"}},
            constantAspectRatio: true,
          };
        }
        if (!child.userData.guiTransform.center) {
          child.userData.guiTransform.center = {x: 0.5, y: 0.5};
        }
        child.center.copy(child.userData.guiTransform.center);
        child.scale.setZ(1);
        if (child.name === "adBannerGui") {
          const size = child.userData.adInfo.size;
          const {x, y} = child.position;
          this.dispatchEvent(
            new CustomEvent(ACTION_TYPE.CHANGE_AD_USED_STATE, {
              detail: {size, bool: true},
            }),
          );
          this.dispatchEvent(
            new CustomEvent(ACTION_TYPE.CHANGE_AD_POSITION, {
              detail: {size, x, y},
            }),
          );
        }
      }
    });

    if (this.runMode !== "play") {
      this.dragControl.dispose();
      this.dragControl = new DragControls([...this.sceneGUI.children], this.guiCamera, this.labelRenderer.domElement);

      this.dragControl.addEventListener("dragstart", this.dragstartSprite);
      this.dragControl.addEventListener(
        "drag",
        _.throttle(() => this.render(), 35),
      );
    }
    this.dragControl.addEventListener("dragend", this.dragendSprite);

    this.dispatchEvent(
      new CustomEvent(ACTION_TYPE.ADD_SCENE, {
        detail: {object: this.sceneGUI},
      }),
    );
    this.reorderZIndex(this.sceneGUI, 0);
    this.resizeHandler();
    this.render();
  }

  setupLatestVersion() {
    // for version 1.1.0+ currently
    if (!this.versionManager.checkCompatibility.avatar.isCreatedByDefault()) {
      // add default camera
      const defaultCamera = this.addCamera("PERSPECTIVE");
      defaultCamera.position.set(0, 5, -15);
      defaultCamera.rotation.set(-Math.PI, 0, -Math.PI);
      defaultCamera.title = defaultCamera.userData.title = "MainCamera";
      defaultCamera.userData.enabled = true;
      this.selectObject(undefined);

      // add preset script
      if (!this.sceneEdit.userData.objectScripts) this.sceneEdit.userData.objectScripts = {serverScripts: [], clientScripts: []};
      this.sceneEdit.userData.objectScripts.clientScripts.push({
        title: "PresetScript",
        source: ScriptLauncher.getPresetScript(this.versionManager.version, {
          renderingMode: this.renderingMode || "3D",
          contentsType: this.contentsType || "Metaverse",
        }),
      });
      this.sceneEdit.userData.canScript = true;
    }
  }

  setTutorial(tutorialInfo) {
    const {missionIdx, completedMissionIdx} = tutorialInfo;
    this.tutorial.updateState(missionIdx, completedMissionIdx);
  }

  // Selfie Functions
  changeShootingMode(shootingMode, setting, offset = {y: 5, forward: 2}) {
    // not in play mode
    if (!this.playControls || !this.avatar) return;
    const targetCamera = this.avatar.followingCamera || this.playCamera;
    if (!targetCamera) return;
    this.playControls.dispose();
    switch (shootingMode) {
      case "NORMAL_MODE":
        this.avatar.changeNicknameVisibility(true);
        this.playControls = new PlayControls(this, targetCamera);
        this.cameraMode = "NORMAL";
        break;
      case "MOUSE_LOCK":
        this.avatar.changeNicknameVisibility(true);
        this.previousCameraOffset.subVectors(targetCamera.position, this.avatar.model.position);
        this.playControls = new PlayControls_MouseLock(
          targetCamera,
          this.avatar.model,
          setting,
          this.labelRenderer.domElement,
          this.previousCameraOffset,
        );
        this.cameraMode = "NORMAL";
        break;
      case "SELFIE_MODE":
        this.avatar.changeNicknameVisibility(true);
        this.previousCameraOffset.subVectors(targetCamera.position, this.avatar.model.position);
        this.playControls = new SelfieControls(this, targetCamera);
        this.cameraMode = "SELFIE";
        this.onSwitchSelfieMode(true);
        break;
      case "FPS_MODE":
        this.avatar.changeNicknameVisibility(false);
        this.previousCameraOffset.subVectors(targetCamera.position, this.avatar.model.position);
        this.playControls = new FirstPersonControls(
          targetCamera,
          [],
          this.avatar.model,
          offset,
          this.labelRenderer.domElement,
          this.previousCameraOffset,
        );
        this.cameraMode = "FPS";
        break;
      case "FOLLOW_MODE":
        this.avatar.changeNicknameVisibility(true);
        this.playControls = new PlayControls(this, targetCamera);
        this.cameraMode = "FOLLOW";
        break;
      default:
        break;
    }
  }
  onSwitchVRMode(isVR) {
    if (isVR) {
      if (this.playControls) this.playControls.dispose();
      this.playControls = new VRControl(
        this.VRCamera,
        this.VRObject,
        this.renderer.glRenderer,
        this.scenePlay,
        this.executeOnclickFunction.bind(this),
        this.handleJoystickDirectionChange.bind(this),
        this.onVRControllerRightSqueezeStart.bind(this),
        this.onVRControllerRightSqueezeEnd.bind(this),
        this.onVRControllerLeftSqueezeStart.bind(this),
        this.onVRControllerLeftSqueezeEnd.bind(this),
      );
      this.cameraMode = "VR";
    } else {
      if (this.playControls) this.playControls.dispose();
      const targetCamera = this.avatar?.followingCamera || this.playCamera;
      if (targetCamera) {
        this.playControls = new PlayControls(this, targetCamera);
      }
      this.cameraMode = "NORMAL";
    }
  }
  onSwitchSelfieMode(isSelfieOn) {
    if (!this.avatar) return;
    this.avatar.toggleSocketByName("selfie_stick");
    this.guiCamera.layers.toggle(4);
    this.toggleVisibility(this.sceneGUI, !isSelfieOn);
    this.avatar.blendAnimation(AVATAR_ANIMATION.SELFIE, isSelfieOn);
  }

  blobToBase64 = blob =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });

  createSelfieLayout() {
    const imageElement = document.createElement("div");
    document.querySelector("#studio").appendChild(imageElement);
    imageElement.style.cssText =
      "position: absolute; width: 70%; height: 70%; background: #000000; text-align: center; display: flex; justify-contenty: center; align-items: flex-end; opacity:0;";
    imageElement.id = "selfie-save-picture";
    const studioEl = document.querySelector("#studio");
    studioEl.style.cssText = "background: rgba(0, 0, 0, 0.7); backdrop-filter: blur(44px); -webkit-backdrop-filter: blur(44px);";
    const image = document.createElement("img");
    imageElement.appendChild(image);
    image.style.cssText = "height: 100%; max-width: 100%; object-fit: contain;";

    image.src = this.selfieUrl;
    imageElement.style.opacity = 1;

    const saveBtn = document.createElement("button");
    imageElement.appendChild(saveBtn);
    saveBtn.style.cssText = "display: flex; flex-direction: column; align-items: center; margin-left: 12px; background-color: transparent";

    const saveBtnImg = document.createElement("img");
    saveBtn.appendChild(saveBtnImg);
    saveBtnImg.style.cssText = "width: 30px; height: 30px";

    const saveBtnText = document.createElement("p");
    saveBtnText.innerText = "SAVE";
    saveBtn.appendChild(saveBtnText);
    saveBtnText.style.cssText = "margin-top: 6px; white-space: nowrap; font-weight: 400; font-size: 12px; color: white;";
    saveBtnImg.src = saveSelfieImage;
    saveBtn.onclick = this.saveSelfie;
  }

  async takeSelfie(type) {
    this.selfieCloseClicked = false;
    this.selfieFile = "";
    const date = new Date();
    let year = date.getFullYear().toString();
    let month = (date.getMonth() + 1).toString();
    let day = date.getDate().toString();
    let hour = date.getHours().toString();
    let minutes = date.getMinutes().toString();
    let seconds = date.getSeconds().toString();
    let nickName = "";
    if (month.length === 1) month = "0" + month;
    if (day.length === 1) day = "0" + day;
    if (hour.length === 1) hour = "0" + hour;
    if (minutes.length === 1) minutes = "0" + minutes;
    if (this.userInfo !== undefined) nickName = this.userInfo.name;

    this.selfieFile = year + month + day + "_" + hour + minutes + seconds + "_" + nickName;

    const joystick = document.querySelector("#joystick");
    const jumpButton = document.querySelector("#joystick_button_J");
    if (joystick) joystick.style.visibility = "hidden";
    if (jumpButton) jumpButton.style.visibility = "hidden";

    switch (type) {
      case "PICTURE":
        if (this.avatar?.model !== undefined) {
          if (isMobile) {
            this.canvas.toBlob(async blob => {
              this.selfieUrl = await this.blobToBase64(blob);
              if (this.selfieCloseClicked) {
                return;
              } else {
                this.createSelfieLayout();
              }
            });
          } else {
            this.selfieUrl = this.canvas.toDataURL("image/png");
            this.createSelfieLayout();
          }

          this.canvas.style.opacity = 0.33;
        }
        break;
      case "VIDEO":
        if (this.mediaRecorder) return;
        const arrVideoData = [];

        const mediaStream = this.canvas.captureStream(30);
        this.mediaRecorder = new MediaRecorder(mediaStream);

        this.mediaRecorder.ondataavailable = event => {
          arrVideoData.push(event.data);
        };

        this.mediaRecorder.onstop = async event => {
          const blob = new Blob(arrVideoData);
          this.selfieVideoBase64 = await this.blobToBase64(blob);
          this.selfieVideoUrl = window.URL.createObjectURL(blob);

          const videoElement = document.createElement("div");

          videoElement.style.cssText =
            "position: absolute; width: 70%; height: 70%; background: #000000; text-align: center; display: flex; align-items: flex-end;";
          videoElement.id = "selfie-save-video";

          const video = document.createElement("video");
          const source = document.createElement("source");
          const macSource = document.createElement("source");
          video.style.cssText = "width: 100%; height: 100%;";
          video.appendChild(source);
          video.appendChild(macSource);
          source.src = this.selfieVideoUrl;
          macSource.src = this.selfieVideoUrl;
          video.controls = true;
          video.autoplay = true;
          source.type = "video/webm";
          macSource.type = "video/mp4";
          video.crossOrigin = "anonymous";
          videoElement.appendChild(video);

          const studioEl = document.querySelector("#studio");
          studioEl.style.cssText = "background: rgba(0, 0, 0, 0.7); backdrop-filter: blur(44px); -webkit-backdrop-filter: blur(44px);";

          studioEl.appendChild(videoElement);

          this.canvas.style.opacity = 0.33;

          const saveBtn = document.createElement("button");
          videoElement.appendChild(saveBtn);
          saveBtn.style.cssText =
            "display: flex; flex-direction: column; justify-content: flex-end; align-items: center; margin-left: 12px; width: 100px; height: 100px; cursor: pointer; background-color:transparent";

          const saveBtnImg = document.createElement("img");
          saveBtn.appendChild(saveBtnImg);
          saveBtnImg.style.cssText = "width: 30px; height: 30px ";

          const saveBtnText = document.createElement("p");
          saveBtnText.innerText = "SAVE";
          saveBtn.appendChild(saveBtnText);
          saveBtnText.style.cssText = "margin-top: 6px; white-space: nowrap; font-weight: 400; font-size: 12px; color: white;";

          saveBtnImg.src = saveSelfieImage;

          saveBtn.onclick = this.saveSelfie;
          arrVideoData.splice(0);
        };

        this.mediaRecorder.start(500);
        break;
      default:
        break;
    }
  }

  saveSelfie_(event) {
    const a = document.createElement("a");
    a.href = this.selfieUrl;
    const savePictureButton = document.querySelector("#selfie-save-picture");

    if (savePictureButton) {
      a.href = this.selfieUrl;
      a.download = `${this.selfieFile}.png`;
    } else {
      a.href = this.selfieVideoBase64;
      a.download = `${this.selfieFile}.${isMacOs ? "mp4" : "webm"}`;
    }
    a.click();
  }

  stopRecord() {
    if (this.mediaRecorder === undefined) return;
    this.mediaRecorder.stop();
    delete this.mediaRecorder;
  }

  hideAvatars() {
    this.playCamera.layers.toggle(2);
  }

  closeSaveScreen(type) {
    // need logic diverting
    this.selfieCloseClicked = true;

    let element = undefined;
    if (type === SELFIE.PICTURE) element = document.getElementById("selfie-save-picture");
    else element = document.getElementById("selfie-save-video");

    if (element) element.remove();
    // this.canvas.style.opacity = 1;
    const joystickEl = document.querySelector("#joystick");
    const jumpButtonEl = document.querySelector("#joystick_button_J");
    if (joystickEl) joystickEl.style.visibility = "visible";
    if (jumpButtonEl) jumpButtonEl.style.visibility = "visible";
    const studioEl = document.querySelector("#studio");
    studioEl.style.cssText = "background: transparent; backdrop-filter: blur(0);";
    const canvasEl = document.querySelector("#canvas");
    canvasEl.style.opacity = "";
  }

  switchSelfieHand(isRightHand) {
    if (this.playCamera) {
      const offsetValue = `${isRightHand ? "" : "-"}${this.selfieCamWidth}`;

      this.playCamera.setViewOffset(
        this.canvas.clientWidth,
        this.canvas.clientHeight,
        offsetValue,
        this.selfieCamHeight,
        this.canvas.clientWidth,
        this.canvas.clientHeight,
      );

      if (this.playControls && this.avatar) {
        const camPosition = new THREE.Vector3();
        this.avatar.model.getWorldDirection(camPosition);
        const defaultAngle = isRightHand ? -35 : 35;
        camPosition.applyAxisAngle(new THREE.Vector3(0, 1, 0), (defaultAngle * Math.PI) / 180);
        camPosition.multiplyScalar(5);

        this.playCamera.position.set(
          this.avatar.model.position.x + camPosition.x,
          this.avatar.model.position.y + camPosition.y,
          this.avatar.model.position.z + camPosition.z,
        );

        this.playControls.orbit.target = new THREE.Vector3(
          this.avatar.model.position.x,
          this.avatar.model.position.y + 3,
          this.avatar.model.position.z,
        );
        this.playControls.orbit.update();
      }
    }
  }

  raycastCamObstructions() {
    // Camera Collide Check
    if (!this.avatar?.model) return;
    this.rayOriginV3.set(this.avatar.model.position.x, this.avatar.model.position.y + 2, this.avatar.model.position.z);

    this.playCameraRaycaster.set(
      // origin
      this.rayOriginV3,
      // direction
      this.rayDirV3.subVectors(this.playCamera.position, this.rayOriginV3).normalize(),
    );

    this.playCameraRaycaster.far = this.rayOriginV3.distanceTo(this.playCamera.position);

    const intersects = this.playCameraRaycaster.intersectObjects(this.scenePlayObjects, true);

    // [option1] object fading
    if (this.objectObstructionOptions[0]) {
      const prevTargetObjects = [...new Set(this.playCamIntersects.map(i => i.object))];
      const targetObjects = [...new Set(intersects.map(i => i.object))];
      this.unapplyShader(prevTargetObjects.filter(obj1 => !targetObjects.find(obj2 => obj1 === obj2)));
      this.applyShader(targetObjects.filter(obj1 => !prevTargetObjects.find(obj2 => obj1 === obj2)));
      this.playCamIntersects = intersects;
    }

    // [option2] camera colliding
    if (this.objectObstructionOptions[1]) {
      if (intersects.length > 0) {
        if (this.camDistance === 0) this.camDistance = this.rayOriginV3.distanceTo(this.playCamera.position);
        this.playCamera.position.lerp(intersects[0].point, 0.15);
      } else {
        if (this.camDistance) {
          this.rayDirV3 = this.playCamera.position.clone().sub(this.rayOriginV3);
          this.playCamera.position.addVectors(this.rayOriginV3, this.rayDirV3.setLength(this.camDistance));
          this.camDistance = 0;
        }
      }
    }

    // [option3] silhouette
    if (this.objectObstructionOptions[2]) {
      if (!this.isSilhouetteReady) {
        this.isSilhouetteReady = true;

        const silhouetteMaterial = new THREE.MeshBasicMaterial({
          stencilWrite: true,
          stencilRef: 2,
          stencilFunc: THREE.AlwaysStencilFunc,
          stencilZPass: THREE.ReplaceStencilOp,
          depthWrite: false,
          depthFunc: THREE.GreaterDepth,
          color: new THREE.Color(0x040404),
          fog: false,
        });

        this.avatarSilhouette = new THREE.Group();
        this.avatarSilhouette.name = "silhouette";

        this.avatar.model.traverse(child => {
          if (child.type === "SkinnedMesh") {
            const clonedSkin = child.clone();
            clonedSkin.position.set(0, 0, 0);
            clonedSkin.material = silhouetteMaterial;
            clonedSkin.castShadow = false;
            clonedSkin.receiveShadow = false;
            this.avatarSilhouette.add(clonedSkin);
          }
          if (child.material) {
            child.material.stencilRef = 2;
            child.material.stencilFunc = THREE.NotEqualStencilFunc;
          }
        });

        this.avatarSilhouette.renderOrder = 0;
        this.avatar.model.renderOrder += 1;

        this.avatar.model.add(this.avatarSilhouette);
      }
    }

    // [option4] outline
    if (this.objectObstructionOptions[3]) {
      if (!this.isOutlineReady) {
        this.isOutlineReady = true;
        this.renderer.createOutline(this.scenePlay, this.playCamera, this.avatar.model);
      }
      if (intersects.length) {
        this.renderer.activateOutline(true);
      } else {
        this.renderer.activateOutline(false);
      }
    }
  }

  applyShader(targets) {
    targets.forEach(target => {
      if (target.material.transparent === false || target.material.opacity > 0.15) {
        const fadedMaterial = target.material.clone();
        fadedMaterial.transparent = true;
        fadedMaterial.opacity = 0.15;
        target.renderOrder = 1;
        target.materialBeforeFaded = target.material;
        target.material = fadedMaterial;
        // [Shader]
        // switch(target.material.type) {
        //   case "MeshPhongMaterial":
        //     target.material.onBeforeCompile = function (shader) {
        //       shader.fragmentShader = MeshPhongDistanceOpacityShader;
        //     };
        //     break;
        //   case "MeshStandardMaterial":
        //     target.material.onBeforeCompile = function (shader) {
        //       shader.fragmentShader = MeshStandardDistanceOpacityShader;
        //     }
        //     break;
        //   default:
        //     break;
        // }
      }
    });
  }

  unapplyShader(targets) {
    targets.forEach(target => {
      if (target.materialBeforeFaded) {
        target.material = target.materialBeforeFaded;
        target.renderOrder = 0;
        delete target.materialBeforeFaded;
        // [Shader]
        // if (target.material) {
        //   target.material.transparent = target.material.opacity !== 1;
        //   delete target.material.onBeforeCompile;
        // }
      }
    });
  }

  updateCameraOrder() {
    if (!this.scenePlayCameras.length) return;

    const useUserData = this.scenePlayCameras[0].depth === undefined;

    if (useUserData) this.scenePlayCameras.sort((a, b) => a.userData.depth - b.userData.depth);
    else this.scenePlayCameras.sort((a, b) => a.depth - b.depth);
  }

  selectScript(currentScript) {
    if (!this.versionManager.checkCompatibility.script.inObject()) return;
    const object =
      this.sceneEdit.getObjectByProperty("uuid", currentScript.objUuid) || this.sceneGUI.getObjectByProperty("uuid", currentScript.objUuid);
    const type = currentScript.type === "serverScript" ? "serverScripts" : "clientScripts";
    this.selectedScript = object.userData.objectScripts[type][currentScript.index];
  }

  updateScript(payload) {
    if (this.codeEditorMode === EDITORMODE.JAVASCRIPT_3D) {
      this.sceneEdit.userData.scriptJS = payload.code;
    } else {
      this.sceneEdit.userData.script = payload.code;
      this.sceneEdit.userData.oobcJSON = payload.json;
    }
  }

  async runScript() {
    if (this.codeEditorMode === EDITORMODE.JAVASCRIPT_3D) {
      if (!this.scriptLauncher) {
        this.scriptLauncher = this.scriptManager.getScriptLauncher(this.versionManager.checkCompatibility.script.isOOP());
      }
      await this.scriptLauncher.init();
      try {
        await this.scriptLauncher.generateUserScript();
        await this.scriptLauncher.runUserScript();
        if (window.SetupFunction) await window.SetupFunction();
        if (window.OnJoinPlayerFunction) await window.OnJoinPlayerFunction(this.scriptLauncher.getPlayer());
      } catch (error) {
        const stack = error.stack.split("\n");
        let message;
        let cause;
        let matches;
        if (stack[0].at(-1) === ")") {
          const subStack = stack[0].split(" ");
          const lineInfo = subStack.pop();
          message = subStack.join(" ");
          matches = lineInfo.match(/\d+/g);
          if (matches) {
            window.alert(`${message} (line ${matches[0]}:${matches[1]})`);
          } else {
            window.alert(`${message}`);
          }
        } else {
          message = stack[0];
          cause = stack[1].split("(")[0];
          matches = stack[1].match(/\d+/g);
          if (matches) {
            window.alert(`${message} \n ${cause} (line ${matches[0] - 8}:${matches[1]})`);
          } else {
            window.alert(`${message}`);
          }
        }
      }
    } else {
      if (!this.oobcApi) {
        this.oobcApi = new OOBCApi(this);
      }
      if (!this.gameServer.serverReady) this.oobcApi.setCameraProps();
      if (this.scenePlay.userData.script.length > 0) {
        try {
          this.oobcApi.script = this.scenePlay.userData.script;
          this.oobcApi.runUserScript();
        } catch (e) {
          console.error(e);
        }
      }
    }
  }

  render() {
    let dt = this.clock.getDelta();
    if (this.showStats) {
      this.stats.update();
      this.debuggerUI.update();
      this.renderer.glRenderer.info.reset();
    }

    if (this.mode === MODE_TYPE.EDIT_MODE) {
      // this.dispatchEvent(
      //   new CustomEvent(ACTION_TYPE.SCENE_INFO, {
      //     detail: {
      //       geometries: this.renderer.glRenderer.info.memory.geometries,
      //       textures: this.renderer.glRenderer.info.memory.textures,
      //       drawCall: this.renderer.glRenderer.info.render.calls,
      //       triangles: this.renderer.glRenderer.info.render.triangles,
      //     },
      //   }),
      // );
      this.renderer.glRenderer.info.reset();
      if (this.runMode === "play") return;
      if (this.showGrid === true) {
        this.sceneEdit.add(this.grid);
      }
      this.renderer.render(this.sceneEdit, this.editCamera);
      this.labelRenderer.render(this.sceneGUI, this.guiCamera);
      if (this.showGrid === true) {
        this.sceneEdit.remove(this.grid);
      }
      if (this.showSceneHelpers === true) {
        this.renderer.render(this.sceneHelpers, this.editCamera, false);
        if (this.viewHelper) {
          if (this.viewHelper.animating && !this.animationFrameCallbacks.viewHelper) {
            this.animationFrameCallbacks.viewHelper = () => {
              this.viewHelper.update(0.016);
              this.render();
              if (!this.viewHelper.animating) delete this.animationFrameCallbacks.viewHelper;
            };
          }
          this.viewHelper.render(this.renderer.glRenderer);
        }
      }
      if (this.orbit && this.orbit.enableDamping && !this.animationFrameCallbacks.orbitDamping) {
        this.animationFrameCallbacks.orbitDamping = () => {
          this.orbit.keyPanUpdate();
          this.orbit.update();
          if (!this.orbit.enableDamping) delete this.animationFrameCallbacks.orbitDamping;
        };
      }
      this.renderer.render(this.sceneGUI, this.guiCamera, false);
      if (this.editParticleSystemArray.length > 0) {
        this.editParticleSystemArray.forEach(system => system.update());
      }
      if (this.selected?.isCamera) {
        const previewCamera = this.selected;
        if (previewCamera.isPerspectiveCamera && previewCamera.aspect !== this.canvas.clientWidth / this.canvas.clientHeight) {
          previewCamera.aspect = this.canvas.clientWidth / this.canvas.clientHeight;
          previewCamera.updateProjectionMatrix();
        }
        this.renderer.glRenderer.setRenderTarget(this.previewRenderTarget);
        this.renderer.glRenderer.clear();
        this.renderer.glRenderer.render(this.sceneEdit, previewCamera);
        this.renderer.glRenderer.setRenderTarget(null);
        this.renderer.glRenderer.render(this.previewScene, this.guiCamera);
      }
    } else {
      if (this.versionManager.checkCompatibility.avatar.isCreatedByDefault() && this.isAvatarReady === false) {
        return;
      }
      if (this.pathTracers) {
        this.pathTracers.forEach(tracer => {
          tracer.update(dt);
        });
      }
      if (this.NPCs) {
        this.NPCs.forEach(NPC => {
          NPC.update(dt);
        });
      }
      if (this.renderer.glRenderer.xr.isPresenting) {
        // this.renderer.glRenderer.xr.getCamera().cameras[0]?.layers.enableAll();
        this.renderer.glRenderer.xr.getCamera().cameras[1]?.layers.enableAll();
        if (this.VRCamera) this.renderer.glRenderer.render(this.scenePlay, this.VRCamera);
      } else {
        this.scenePlayCameras.forEach(cam => {
          if (!cam.enabled) return;
          cam.renderer.render(this.scenePlay, cam);
        });

        if (this.scenePlayGUI && this.codeEditorMode !== EDITORMODE.JAVASCRIPT_3D) {
          this.labelRenderer.render(this.scenePlayGUI, this.guiCamera);
          this.renderer.render(this.scenePlayGUI, this.guiCamera, false);
        } else {
          this.labelRenderer.render(this.sceneGUI, this.guiCamera);
          this.renderer.render(this.sceneGUI, this.guiCamera, false);
        }
      }
      if (this.particleSystemArray.length > 0) {
        this.particleSystemArray.forEach(system => system.update());
      }
      if (this.playControls?.isFPSControls) {
        this.playControls.update(dt);
      }
      if (this.physics !== undefined) {
        if (this.codeEditorMode === EDITORMODE.JAVASCRIPT_3D) {
          try {
            this.scriptLauncher?.update(dt);
          } catch (error) {
            window.alert(`${error.name} occurred in the Update function: ${error.message}`);
            console.error(`${error.name} occurred in the Update function: ${error.message}`);
            this.scriptLauncher.dispose();
          }
        }
        TWEEN.update();
        this.physics.update(dt * 1000);
        if (process.env.NODE_ENV === "development") {
          if (this.physics.debugDrawer.enabled) {
            this.physics.updateDebugger();
          }
        }
        if (this.isAvatarReady) {
          this.animateAvatar(dt);
          this.avatar?.render(dt);
        }
      }
      if (this.runMode === "play") {
        // Colyseus send info to server
        this.gameServer.sendInputToServer("move");
        this.gameServer.updateSyncObjectsMovement(dt);
        // this.sendMovement();
        // this.updateMovement(dt);
      }
      if (this.tutorial) {
        this.tutorial.update();
      }
      // if (
      //   this.pId !== "725c3089-488a-4820-bc1f-eb92114c6e46" &&
      //   this.pId !== "c828f988-df02-4227-a3ab-b83218411e7d" &&
      //   this.pId !== "8a94cf8d-4e25-4af1-8ac1-27fd5daf21e8"
      // ) {
      // this.raycastCamObstructions();
      // }
    }

    // return this.canvas;
    // if (this.spriteGroup) this.spriteGroup.position.set(this.editCamera.position.x, this.editCamera.position.y, this.editCamera.position.z - 10);
    return true;
  }

  setEmoteAnimation(name) {
    if (this.avatar && !this.avatar.isJumping) {
      this.avatar.emoteAnimation = name;
      if (this.gameServer.room) {
        this.gameServer.room.send(CLIENT_MESSAGE.AVATAR_EMOTE_CHANGED, name);
      }
    }
  }

  animateAvatar(dt) {
    if (this.avatar !== undefined) {
      let animationName;
      if (this.avatar.isJumping) {
        animationName = AVATAR_ANIMATION.JUMP;
      } else if (!this.avatar.isFalling) {
        animationName = AVATAR_ANIMATION.IDLE;
        // if (this.cameraMode === "SELFIE") animationName = AVATAR_ANIMATION.SELFIE;
        if (
          this.moveDirection.forward === 1 ||
          this.moveDirection.right === 1 ||
          this.moveDirection.left === 1 ||
          this.moveDirection.back === 1 ||
          this.joystickRadian !== 0
        ) {
          animationName = AVATAR_ANIMATION.RUN;
          if (this.avatar.speedFactor > 1) animationName = AVATAR_ANIMATION.BOOST;
        }
        if (this.avatar.emoteAnimation) {
          animationName = this.avatar.emoteAnimation;
        }
      }
      if (this.avatar.customAnimation) {
        animationName = this.avatar.customAnimation;
      }
      if (!this.avatar.isFalling) this.avatar.playAnimation(animationName);
    }
  }

  undoRedo(action) {
    _undoRedo(this, action);
  }

  dispose() {
    this.objects = [];
    this.assetObjects = {};
    this.undo = [];
    this.redo = [];
    // there is no dispose method in Renderer.js
    // if (this.renderer) this.renderer.dispose();
  }

  async handleFileInput(selectedFile) {
    let compressed = await ImageCompressor(selectedFile);
    let image = window.URL.createObjectURL(compressed);
    const texture = this.TextureLoader.load(image, () => {
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.mapping = THREE.EquirectangularReflectionMapping;
      texture.encoding = THREE.sRGBEncoding;
      texture.repeat.set(1, 1);
      this.sceneEdit.background = texture;
      this.dispatchEvent(
        new CustomEvent(ACTION_TYPE.CHANGE_PROPERTY_FROM_SCENE, {
          detail: {object: this.selected},
        }),
      );
      this.render();
    });
  }
}

export default Graphics;
